import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Slider,
  TextField,
  Typography,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import AppsIcon from '@material-ui/icons/Apps';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CallEndIcon from '@material-ui/icons/CallEnd';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import BlurOnIcon from '@material-ui/icons/BlurOn';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import ScreenShareIcon from '@material-ui/icons/ScreenShare';
import SettingsIcon from '@material-ui/icons/Settings';
import StopScreenShareIcon from '@material-ui/icons/StopScreenShare';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { LiveKitRoom, useMaybeRoomContext, useRemoteParticipants } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision';
import moment from 'moment';

import {
  formatVideoconsultationSummaryLabel,
  normalizeVideoconsultationSummary,
} from '../../Landingpagecomponents/Content/videoconsultationSummary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
const MEDIAPIPE_WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm';
const SELFIE_SEGMENTER_MODEL_URL = '/models/selfie_segmenter.tflite';
const VIDEO_CALL_MICROPHONE_STORAGE_KEY = 'virtus.videoCall.lastMicrophoneDeviceId';
const VIDEO_CALL_SPEAKER_STORAGE_KEY = 'virtus.videoCall.lastSpeakerDeviceId';
const ACTIVE_VIDEO_CALL_SESSION_KEY = 'virtus.videoCall.active';

const readStoredDeviceId = (storageKey) => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(storageKey) || '';
  } catch (error) {
    return '';
  }
};

const writeStoredDeviceId = (storageKey, deviceId) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (deviceId) {
      window.localStorage.setItem(storageKey, deviceId);
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch (error) {
    void error;
  }
};

const BRAND_COLORS = {
  PRIMARY: '#002D55',
  ACCENT: '#fb741a',
  BACKGROUND: '#fafdff',
  PANEL: '#f7faff',
  PANEL_ALT: '#e6eaf0',
  BORDER: '#dce4eb',
  TEXT: '#002D55',
  MUTED: '#64748B',
  SOFT: '#f5f5f5',
  SUCCESS: '#4caf50',
};

const AUDIO_SELECT_MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    style: {
      borderRadius: 14,
      marginTop: 8,
    },
  },
};

const useStyles = makeStyles(() => ({
  paper: {
    background: BRAND_COLORS.BACKGROUND,
  },
  shell: {
    position: 'relative',
    height: '100dvh',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: BRAND_COLORS.BACKGROUND,
  },
  glow: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 18% 18%, rgba(251, 116, 26, 0.08), transparent 28%), radial-gradient(circle at 82% 6%, rgba(0, 45, 85, 0.06), transparent 28%)',
    pointerEvents: 'none',
  },
  header: {
    position: 'relative',
    zIndex: 1,
    padding: '15px 21px',
    background: BRAND_COLORS.PRIMARY,
    backdropFilter: 'blur(18px)',
    borderBottom: `3px solid ${BRAND_COLORS.ACCENT}`,
    '@media (max-width: 1200px)': {
      padding: '6px 9px',
    },
    '@media (max-width: 760px)': {
      padding: '5px 8px',
      borderBottomWidth: 2,
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    '@media (max-width: 1200px)': {
      gap: 8,
    },
    '@media (max-width: 760px)': {
      gap: 5,
      alignItems: 'center',
      flexWrap: 'nowrap',
    },
  },
  headerCopy: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  headerStatus: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
    '@media (max-width: 760px)': {
      display: 'none',
    },
  },
  eyebrow: {
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: 1.4,
    fontSize: 11,
    fontWeight: 800,
  },
  title: {
    marginTop: 0,
    color: '#fff',
    fontWeight: 800,
    lineHeight: 1.06,
    fontSize: 'clamp(0.95rem, 1.7vw, 1.25rem)',
    '@media (max-width: 1200px)': {
      fontSize: 'clamp(0.9rem, 1.4vw, 1.1rem)',
    },
    '@media (max-width: 760px)': {
      marginTop: 2,
      fontSize: 14,
      lineHeight: 1.1,
    },
  },
  subtitle: {
    marginTop: 2,
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 'clamp(0.72rem, 0.85vw, 0.84rem)',
    lineHeight: 1.3,
    maxWidth: 880,
    '@media (max-width: 1200px)': {
      fontSize: 'clamp(0.65rem, 0.72vw, 0.76rem)',
      lineHeight: 1.2,
    },
    '@media (max-width: 760px)': {
      fontSize: 10,
      lineHeight: 1.2,
    },
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
    '@media (max-width: 1200px)': {
      gap: 6,
    },
    '@media (max-width: 760px)': {
      gap: 4,
      flexShrink: 0,
      flexWrap: 'nowrap',
      '& .MuiChip-root': {
        display: 'none',
      },
      '& .MuiIconButton-root': {
        padding: 7,
      },
    },
  },
  statusChip: {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.18)',
    fontWeight: 700,
    fontSize: 'clamp(0.64rem, 0.76vw, 0.8rem)',
    '@media (max-width: 1200px)': {
      fontSize: '0.67rem',
      height: 24,
    },
  },
  darkChip: {
    background: BRAND_COLORS.PANEL_ALT,
    color: BRAND_COLORS.TEXT,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    fontWeight: 700,
  },
  closeButton: {
    color: '#fff',
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.18)',
    },
  },
  headerActionButton: {
    color: '#fff',
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.18)',
    },
  },
  content: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'flex',
    padding: 0,
    minHeight: 0,
    overflow: 'auto',
  },
  layout: {
    flex: 1,
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 18,
    padding: 20,
    '@media (max-width: 1200px)': {
      gap: 10,
      padding: 10,
    },
    '@media (max-width: 1200px)': {
      gap: 14,
      padding: 16,
    },
    '@media (max-width: 760px)': {
      gap: 10,
      padding: 10,
    },
  },
  layoutWithWorkspace: {
    gap: 0,
    '@media (max-width: 1200px)': {
      gridTemplateColumns: '1fr',
      gap: 18,
    },
  },
  workspaceSplitter: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    width: 24,
    margin: '0 -12px',
    position: 'relative',
    zIndex: 8,
    overflow: 'visible',
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  workspaceSplitterHandle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 26,
    height: 44,
    borderRadius: 999,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    background: '#fff',
    color: BRAND_COLORS.PRIMARY,
    boxShadow: '0 8px 18px rgba(0, 45, 85, 0.10)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
    zIndex: 9,
  },
  workspacePanel: {
    display: 'grid',
    gridTemplateRows: 'minmax(0, 1fr)',
    gap: 0,
    minHeight: '100%',
    height: '100%',
    borderRadius: 24,
    padding: 0,
    background: '#fff',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 18px 48px rgba(0, 45, 85, 0.10)',
    overflow: 'hidden',
  },
  workspacePanelPatient: {
    display: 'grid',
    gridTemplateRows: 'minmax(0, 1fr)',
    gap: 0,
    minHeight: '100%',
    height: '100%',
    borderRadius: 0,
    padding: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    overflow: 'hidden',
  },
  workspaceFrameShellPatient: {
    position: 'relative',
    minHeight: 0,
    height: '100%',
    borderRadius: 24,
    overflow: 'hidden',
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
  },
  workspaceHeaderActionButton: {
      color: '#fff',
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      padding: 0,
      minWidth: 0,
      '@media (max-width: 1200px)': {
        '& .MuiSvgIcon-root': {
          fontSize: 18,
        },
      },
    '&:hover': {
        background: 'transparent',
        boxShadow: 'none',
    },
  },
  workspaceHeader: {
    display: 'none',
  },
  workspaceHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  workspaceTitle: {
    color: '#fff',
    fontWeight: 800,
    fontSize: 18,
  },
  workspaceSubtitle: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 12.5,
    lineHeight: 1.45,
  },
  workspaceActionRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    '@media (max-width: 540px)': {
      gridTemplateColumns: '1fr',
    },
  },
  workspaceTargetCard: {
    display: 'grid',
    gap: 6,
    padding: 12,
    borderRadius: 18,
    background: BRAND_COLORS.PANEL,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
  },
  workspaceTargetLabel: {
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 800,
    fontSize: 12.5,
  },
  workspaceTargetBody: {
    color: BRAND_COLORS.MUTED,
    fontSize: 12.5,
    lineHeight: 1.45,
  },
  workspaceFrameShell: {
    position: 'relative',
    minHeight: 0,
    height: '100%',
    borderRadius: 20,
    overflow: 'hidden',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    background: BRAND_COLORS.PANEL,
  },
  workspaceCloseButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 2,
  },
  workspaceFrame: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    border: 0,
    background: '#fff',
  },
  workspaceFrameLoading: {
    position: 'absolute',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255,255,255,0.86)',
    backdropFilter: 'blur(8px)',
    zIndex: 1,
  },
  patientWorkspaceScroll: {
    position: 'relative',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    background: 'transparent',
  },
  patientWorkspaceInner: {
    display: 'grid',
    gap: 14,
    padding: 18,
  },
  patientWorkspaceHeader: {
    display: 'grid',
    gap: 8,
    padding: 16,
    borderRadius: 18,
    background: 'linear-gradient(180deg, rgba(0,45,85,0.96) 0%, rgba(0,45,85,0.90) 100%)',
    color: '#fff',
  },
  patientWorkspaceKicker: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.74)',
  },
  patientWorkspaceTitle: {
    fontWeight: 800,
    fontSize: 18,
    color: '#fff',
  },
  patientWorkspaceSubtitle: {
    fontSize: 13,
    lineHeight: 1.55,
    color: 'rgba(255,255,255,0.84)',
  },
  patientWorkspaceCard: {
    display: 'grid',
    gap: 10,
    padding: 16,
    borderRadius: 18,
    background: BRAND_COLORS.PANEL,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
  },
  patientWorkspaceLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 0.4fr) minmax(0, 1fr)',
    gap: 16,
    alignItems: 'start',
    '@media (max-width: 1100px)': {
      gridTemplateColumns: '1fr',
    },
  },
  patientWorkspaceMenu: {
    display: 'grid',
    gap: 10,
    padding: 16,
    borderRadius: 18,
    background: BRAND_COLORS.PANEL,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    alignSelf: 'start',
  },
  patientWorkspaceMenuLabel: {
    color: BRAND_COLORS.PRIMARY,
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  patientWorkspaceMenuButton: {
    width: '100%',
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 14,
    minHeight: 46,
    padding: '10px 14px',
    color: BRAND_COLORS.TEXT,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    background: '#fff',
    '&:hover': {
      background: 'rgba(251, 116, 26, 0.06)',
      borderColor: BRAND_COLORS.ACCENT,
    },
  },
  patientWorkspaceMenuButtonActive: {
    background: BRAND_COLORS.PRIMARY,
    color: '#fff',
    borderColor: BRAND_COLORS.PRIMARY,
    '&:hover': {
      background: BRAND_COLORS.PRIMARY,
      borderColor: BRAND_COLORS.PRIMARY,
    },
  },
  patientWorkspaceMenuButtonLabel: {
    display: 'grid',
    gap: 2,
    textAlign: 'left',
  },
  patientWorkspaceMenuButtonHint: {
    fontSize: 11,
    fontWeight: 500,
    opacity: 0.75,
    lineHeight: 1.35,
  },
  patientWorkspaceSectionPanel: {
    display: 'grid',
    gap: 14,
    minWidth: 0,
  },
  patientWorkspaceSectionShell: {
    display: 'grid',
    gap: 14,
    minWidth: 0,
    borderRadius: 18,
    background: '#fff',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    padding: 16,
  },
  patientWorkspaceSectionHeader: {
    display: 'grid',
    gap: 6,
  },
  patientWorkspaceSectionTitle: {
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 800,
    fontSize: 16,
  },
  patientWorkspaceSectionSubtitle: {
    color: BRAND_COLORS.MUTED,
    lineHeight: 1.55,
    fontSize: 13,
  },
  patientWorkspaceSectionBody: {
    display: 'grid',
    gap: 14,
    minWidth: 0,
  },
  patientWorkspaceMetricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
    '@media (max-width: 760px)': {
      gridTemplateColumns: '1fr',
    },
  },
  patientWorkspaceMetricCard: {
    display: 'grid',
    gap: 4,
    padding: 14,
    borderRadius: 16,
    background: BRAND_COLORS.PANEL,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
  },
  patientWorkspaceMetricLabel: {
    color: BRAND_COLORS.MUTED,
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  patientWorkspaceMetricValue: {
    color: BRAND_COLORS.PRIMARY,
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.25,
  },
  patientWorkspaceSectionFrame: {
    minWidth: 0,
    minHeight: 560,
    borderRadius: 18,
    overflow: 'hidden',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    background: '#fff',
  },
  patientWorkspaceSectionFrameInner: {
    width: '100%',
    height: '100%',
    minHeight: 560,
  },
  patientWorkspaceSectionIframe: {
    width: '100%',
    height: '100%',
    minHeight: 560,
    border: 0,
    background: '#fff',
  },
  patientWorkspaceCardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 800,
    fontSize: 13,
  },
  patientWorkspaceMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  patientWorkspaceBody: {
    color: BRAND_COLORS.MUTED,
    lineHeight: 1.55,
    fontSize: 13.5,
  },
  patientWorkspaceFeedbackList: {
    display: 'grid',
    gap: 10,
  },
  patientWorkspaceFeedbackItem: {
    display: 'grid',
    gap: 6,
    padding: 12,
    borderRadius: 14,
    background: '#fff',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
  },
  patientWorkspaceFeedbackLabel: {
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 700,
    fontSize: 12,
  },
  patientWorkspaceEmpty: {
    padding: 12,
    borderRadius: 14,
    border: `1px dashed ${BRAND_COLORS.BORDER}`,
    color: BRAND_COLORS.MUTED,
    background: '#fff',
  },
  introPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minHeight: 0,
  },
  heroCard: {
    background: BRAND_COLORS.PANEL,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    borderRadius: 28,
    padding: 24,
    boxShadow: '0 10px 26px rgba(0, 45, 85, 0.08)',
  },
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 18,
  },
  bulletList: {
    listStyle: 'none',
    margin: '18px 0 0',
    padding: 0,
    display: 'grid',
    gap: 12,
  },
  bulletItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    color: BRAND_COLORS.TEXT,
    lineHeight: 1.55,
  },
  bulletIcon: {
    color: BRAND_COLORS.ACCENT,
    marginTop: 2,
    flexShrink: 0,
  },
  descriptionCard: {
    marginTop: 14,
    background: BRAND_COLORS.SOFT,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    borderRadius: 20,
    padding: 16,
  },
  descriptionLabel: {
    color: BRAND_COLORS.PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontSize: 11,
    fontWeight: 800,
  },
  descriptionBody: {
    marginTop: 6,
    color: BRAND_COLORS.TEXT,
    lineHeight: 1.55,
    fontSize: 13.5,
  },
  sessionPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    '@media (max-width: 760px)': {
      gap: 8,
    },
  },
  liveHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '14px 18px',
    borderRadius: 22,
    background: '#fff',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 6px 18px rgba(0, 45, 85, 0.08)',
    '@media (max-width: 1200px)': {
      padding: '8px 12px',
      borderRadius: 16,
    },
    '@media (max-width: 1200px)': {
      padding: '11px 14px',
      borderRadius: 18,
    },
    '@media (max-width: 760px)': {
      gap: 8,
      padding: '8px 10px',
      borderRadius: 14,
    },
  },
  liveHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    minWidth: 0,
    '@media (max-width: 760px)': {
      gap: 7,
    },
  },
  liveDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: BRAND_COLORS.ACCENT,
    boxShadow: '0 0 0 6px rgba(251, 116, 26, 0.14)',
    flexShrink: 0,
    '@media (max-width: 760px)': {
      width: 7,
      height: 7,
      boxShadow: '0 0 0 4px rgba(251, 116, 26, 0.14)',
    },
  },
  liveLabel: {
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 800,
    fontSize: 13,
    letterSpacing: 0.3,
    '@media (max-width: 760px)': {
      fontSize: 11,
    },
  },
  liveTimer: {
    color: BRAND_COLORS.MUTED,
    fontSize: 13,
    fontWeight: 600,
    '@media (max-width: 760px)': {
      fontSize: 11,
    },
  },
  liveActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  liveChip: {
    background: BRAND_COLORS.PANEL_ALT,
    color: BRAND_COLORS.PRIMARY,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    fontWeight: 700,
  },
  secureChip: {
    background: 'rgba(251, 116, 26, 0.12)',
    color: BRAND_COLORS.ACCENT,
    border: '1px solid rgba(251, 116, 26, 0.28)',
    fontWeight: 800,
  },
  endButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    width: 48,
    minWidth: 48,
    height: 48,
    padding: 0,
    display: 'grid',
    placeItems: 'center',
    borderColor: BRAND_COLORS.ACCENT,
    color: BRAND_COLORS.ACCENT,
    fontSize: '0.72rem',
    lineHeight: 1,
    '@media (max-width: 1200px)': {
      width: 42,
      minWidth: 42,
      height: 42,
      fontSize: '0.68rem',
    },
    '&:hover': {
      borderColor: BRAND_COLORS.ACCENT,
      background: 'rgba(251, 116, 26, 0.08)',
    },
    '@media (max-width: 760px)': {
      width: 38,
      minWidth: 38,
      height: 38,
      fontSize: '0.62rem',
    },
  },
  roomCard: {
    flex: 1,
    height: '100%',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    borderRadius: 28,
    overflow: 'hidden',
    background: '#fff',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 10px 26px rgba(0, 45, 85, 0.08)',
    '@media (max-width: 760px)': {
      borderRadius: 16,
    },
  },
  roomInner: {
    height: '100%',
    minHeight: 'clamp(420px, 56vh, 720px)',
    minWidth: 0,
    width: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      minWidth: 0,
      width: '100%',
    },
    '& .lk-video-conference': {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      height: '100%',
      background: '#f3f6fb',
    },
    '& .lk-video-conference-inner': {
      height: '100%',
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    '& .lk-grid-layout-wrapper, & .lk-focus-layout-wrapper': {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      height: '100%',
      background: '#f3f6fb',
    },
    '& .lk-grid-layout, & .lk-focus-layout': {
      height: '100%',
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      alignContent: 'stretch',
      justifyContent: 'stretch',
    },
    '& .lk-participant': {
      borderRadius: 24,
      overflow: 'hidden',
      background: '#0b2747',
    },
    '& .lk-participant video': {
      objectFit: 'contain',
      objectPosition: 'center center',
      backgroundColor: '#0b2747',
    },
    '& .lk-participant-video': {
      objectFit: 'contain',
      objectPosition: 'center center',
      backgroundColor: '#0b2747',
    },
    '& .lk-participant-placeholder': {
      background: 'linear-gradient(180deg, #103255 0%, #002D55 100%)',
    },
    '@media (max-width: 1200px)': {
      minHeight: 'clamp(250px, 34dvh, 400px)',
    },
    '@media (max-width: 1200px)': {
      minHeight: 'clamp(360px, 52dvh, 600px)',
    },
    '@media (max-width: 760px)': {
      minHeight: 'clamp(280px, 46dvh, 440px)',
      '& .lk-participant': {
        borderRadius: 14,
      },
    },
  },
  workspaceCallStage: {
    position: 'relative',
    flex: 1,
    height: '100%',
    minHeight: 'clamp(420px, 56vh, 720px)',
    minWidth: 0,
    width: '100%',
    borderRadius: 28,
    overflow: 'hidden',
    background: '#081d34',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 10px 26px rgba(0, 45, 85, 0.08)',
    '@media (max-width: 1200px)': {
      minHeight: 'clamp(250px, 34dvh, 400px)',
      borderRadius: 20,
    },
    '@media (max-width: 1200px)': {
      minHeight: 'clamp(360px, 52dvh, 600px)',
      borderRadius: 20,
    },
    '@media (max-width: 760px)': {
      minHeight: 'clamp(280px, 46dvh, 440px)',
      borderRadius: 16,
    },
  },
  workspaceCallFrame: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
    background: '#081d34',
  },
  workspaceCallFallback: {
    position: 'absolute',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    textAlign: 'center',
    color: '#fff',
    background: 'linear-gradient(180deg, rgba(16, 50, 85, 0.92) 0%, rgba(0, 45, 85, 0.98) 100%)',
  },
  workspacePiP: {
    position: 'absolute',
    left: 14,
    bottom: 14,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 18,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(0, 45, 85, 0.88)',
    boxShadow: '0 14px 32px rgba(0, 0, 0, 0.24)',
    backdropFilter: 'blur(10px)',
    '@media (max-width: 760px)': {
      left: 8,
      bottom: 8,
      borderRadius: 12,
    },
  },
  workspacePiPLabel: {
    padding: '8px 10px',
    color: '#fff',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.3,
    background: 'rgba(255,255,255,0.08)',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
  },
  workspacePiPBody: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    background: '#000',
  },
  workspacePiPVideo: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
    background: '#000',
  },
  workspacePiPPlaceholder: {
    display: 'grid',
    placeItems: 'center',
    height: '100%',
    padding: 12,
    color: '#fff',
    textAlign: 'center',
    background: 'linear-gradient(180deg, rgba(16, 50, 85, 0.92) 0%, rgba(0, 45, 85, 0.98) 100%)',
  },
  workspacePiPResizeHandle: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 18,
    height: 18,
    borderRadius: 6,
    cursor: 'nwse-resize',
    zIndex: 3,
    background: 'rgba(255,255,255,0.22)',
    border: '1px solid rgba(255,255,255,0.34)',
    display: 'grid',
    placeItems: 'center',
    color: '#fff',
  },
  callStage: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    width: '100%',
    background: '#0b2747',
    padding: 16,
    '@media (max-width: 760px)': {
      padding: 8,
    },
  },
  callGrid: {
    height: '100%',
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 0.78fr) minmax(320px, 1.22fr)',
    gap: 16,
    '@media (max-width: 980px)': {
      gridTemplateColumns: '1fr',
    },
  },
  tilePanel: {
    position: 'relative',
    minHeight: 0,
    borderRadius: 22,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    background: '#081d34',
    boxShadow: '0 18px 38px rgba(0, 0, 0, 0.24)',
    display: 'flex',
    flexDirection: 'column',
  },
  tileHeader: {
    padding: '10px 12px',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.3,
    background: 'rgba(255,255,255,0.08)',
  },
  tileSurface: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    background: '#000',
    overflow: 'hidden',
  },
  panelPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    padding: 24,
    textAlign: 'center',
    color: '#fff',
    background: 'linear-gradient(180deg, rgba(16, 50, 85, 0.92) 0%, rgba(0, 45, 85, 0.98) 100%)',
  },
  panelPlaceholderTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: '#fff',
  },
  panelPlaceholderBody: {
    marginTop: 8,
    fontSize: 13.5,
    lineHeight: 1.55,
    color: 'rgba(255,255,255,0.82)',
  },
  selfViewVideo: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center center',
    background: '#000',
  },
  selfViewStage: {
    position: 'relative',
    width: '100%',
    height: '100%',
    background: '#000',
  },
  selfViewVideoSource: {
    position: 'absolute',
    inset: 0,
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: 'none',
  },
  selfViewCanvas: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center center',
    background: '#000',
  },
  selfViewFallbackNotice: {
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 12,
    padding: '8px 10px',
    borderRadius: 12,
    background: 'rgba(8, 29, 52, 0.78)',
    border: '1px solid rgba(255,255,255,0.16)',
    backdropFilter: 'blur(10px)',
  },
  remoteStatusList: {
    display: 'grid',
    gap: 10,
    marginTop: 12,
  },
  remoteStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff',
    textAlign: 'left',
  },
  remoteStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: BRAND_COLORS.ACCENT,
    boxShadow: '0 0 0 5px rgba(251, 116, 26, 0.14)',
    flexShrink: 0,
  },
  remoteStatusText: {
    display: 'grid',
    gap: 2,
  },
  remoteStatusTitle: {
    color: '#fff',
    fontWeight: 800,
    fontSize: 13,
  },
  remoteStatusBody: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 12.5,
    lineHeight: 1.45,
  },
  preflightShell: {
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    background: 'rgba(0, 45, 85, 0.14)',
    backdropFilter: 'blur(10px)',
  },
  preflightPanel: {
    width: 'min(1120px, 100%)',
    borderRadius: 28,
    padding: 18,
    background: '#fff',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 18px 48px rgba(0, 45, 85, 0.18)',
    display: 'grid',
    gap: 16,
  },
  overlayHeader: {
    borderRadius: 24,
    padding: 18,
    background: BRAND_COLORS.PANEL,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    display: 'grid',
    gap: 12,
  },
  overlayTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  overlayTitle: {
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 800,
    fontSize: 18,
  },
  overlayBody: {
    color: BRAND_COLORS.MUTED,
    lineHeight: 1.55,
    fontSize: 13.5,
  },
  overlayGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)',
    gap: 16,
    alignItems: 'stretch',
    '@media (max-width: 980px)': {
      gridTemplateColumns: '1fr',
    },
  },
  preflightGrid: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: 0,
    '@media (max-width: 1200px)': {
      minHeight: 'clamp(360px, 52dvh, 600px)',
    },
  },
  quickstartWrap: {
    width: 'min(920px, 100%)',
    display: 'grid',
    gap: 10,
  },
  quickstartButton: {
    justifySelf: 'start',
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 800,
    padding: '9px 18px',
    background: '#fff',
    color: BRAND_COLORS.PRIMARY,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 8px 18px rgba(0, 45, 85, 0.08)',
    '&:hover': {
      background: BRAND_COLORS.PANEL,
      borderColor: BRAND_COLORS.ACCENT,
    },
  },
  quickstartHint: {
    color: BRAND_COLORS.MUTED,
    fontSize: 12.5,
    lineHeight: 1.5,
  },
  quickstartPaper: {
    borderRadius: 28,
    overflow: 'hidden',
  },
  quickstartDialog: {
    padding: 0,
    background: BRAND_COLORS.BACKGROUND,
  },
  quickstartHeader: {
    padding: '20px 22px 16px',
    background: BRAND_COLORS.PRIMARY,
    color: '#fff',
    display: 'grid',
    gap: 10,
  },
  quickstartTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  quickstartTitle: {
    color: '#fff',
    fontWeight: 800,
    fontSize: 20,
  },
  quickstartBrowserPill: {
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.18)',
    fontWeight: 800,
  },
  quickstartIntro: {
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.55,
    fontSize: 13.5,
  },
  quickstartBody: {
    padding: 22,
    display: 'grid',
    gap: 14,
  },
  quickstartNotice: {
    borderRadius: 18,
    padding: 14,
    background: 'rgba(251, 116, 26, 0.08)',
    border: '1px solid rgba(251, 116, 26, 0.18)',
    color: BRAND_COLORS.TEXT,
    lineHeight: 1.55,
    fontSize: 13,
  },
  quickstartStepList: {
    display: 'grid',
    gap: 12,
  },
  quickstartStepRow: {
    display: 'grid',
    gridTemplateColumns: '36px minmax(0, 1fr)',
    gap: 12,
    alignItems: 'start',
  },
  quickstartStepNumber: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: BRAND_COLORS.PRIMARY,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 14,
  },
  quickstartStepCard: {
    borderRadius: 18,
    padding: 14,
    background: '#fff',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 6px 16px rgba(0, 45, 85, 0.06)',
  },
  quickstartStepTitle: {
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 800,
    fontSize: 14,
  },
  quickstartStepBody: {
    marginTop: 4,
    color: BRAND_COLORS.MUTED,
    lineHeight: 1.55,
    fontSize: 13,
  },
  quickstartFooter: {
    padding: '0 22px 22px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  toolbarWrap: {
    width: 'min(920px, 100%)',
    margin: '0 auto',
    display: 'grid',
    gap: 10,
    '@media (max-width: 1200px)': {
      gap: 6,
    },
  },
  actionToolbar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 10,
    width: '100%',
    padding: 12,
    borderRadius: 22,
    background: 'rgba(0, 45, 85, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)',
    backdropFilter: 'blur(12px)',
    '@media (max-width: 1200px)': {
      gap: 8,
      padding: 8,
      borderRadius: 18,
    },
    '@media (max-width: 760px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 6,
      padding: 6,
      borderRadius: 14,
    },
  },
  actionToolbarButton: {
    minHeight: 48,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 'clamp(0.67rem, 0.78vw, 0.84rem)',
    display: 'flex',
    gap: 6,
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px 10px',
    minWidth: 0,
    '&:hover': {
      background: 'rgba(255,255,255,0.12)',
      borderColor: 'rgba(255,255,255,0.18)',
    },
    '@media (max-width: 1200px)': {
      minHeight: 36,
      padding: '4px 6px',
      gap: 4,
      fontSize: '0.72rem',
      '& .MuiSvgIcon-root': {
        fontSize: 18,
      },
    },
    '@media (max-width: 760px)': {
      minHeight: 38,
      padding: 6,
      borderRadius: 10,
      '& .MuiSvgIcon-root': {
        fontSize: 20,
      },
    },
  },
  actionToolbarButtonActive: {
    background: 'rgba(251, 116, 26, 0.18)',
    borderColor: 'rgba(251, 116, 26, 0.4)',
    color: '#fff',
  },
  actionToolbarButtonLabel: {
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
    fontSize: 'clamp(0.68rem, 0.78vw, 0.82rem)',
    '@media (max-width: 1200px)': {
      fontSize: '0.72rem',
    },
    '@media (max-width: 760px)': {
      display: 'none',
    },
  },
  actionToolbarButtonHint: {
    fontSize: 'clamp(0.58rem, 0.65vw, 0.72rem)',
    opacity: 0.8,
    marginTop: 2,
    whiteSpace: 'nowrap',
    '@media (max-width: 1200px)': {
      fontSize: '0.6rem',
      marginTop: 1,
    },
    '@media (max-width: 760px)': {
      display: 'none',
    },
  },
  toolbarButtonIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  drawerPaper: {
    width: 'min(430px, 100vw)',
    borderTopLeftRadius: 24,
    borderBottomLeftRadius: 24,
    '@media (max-width: 760px)': {
      width: '100%',
      maxHeight: '82dvh',
      borderTopRightRadius: 20,
      borderBottomLeftRadius: 0,
    },
  },
  drawerHeader: {
    padding: '18px 20px 14px',
    borderBottom: `1px solid ${BRAND_COLORS.BORDER}`,
    display: 'grid',
    gap: 4,
    '@media (max-width: 760px)': {
      padding: '12px 14px 10px',
    },
  },
  drawerTitle: {
    fontWeight: 800,
    color: BRAND_COLORS.PRIMARY,
    fontSize: 'clamp(0.95rem, 1.05vw, 1.05rem)',
  },
  drawerSubtitle: {
    color: BRAND_COLORS.MUTED,
    fontSize: 'clamp(0.72rem, 0.82vw, 0.82rem)',
    lineHeight: 1.45,
  },
  drawerBody: {
    padding: 20,
    display: 'grid',
    gap: 18,
    '@media (max-width: 760px)': {
      padding: 14,
      gap: 12,
    },
  },
  drawerSection: {
    display: 'grid',
    gap: 12,
    padding: 16,
    borderRadius: 20,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    background: BRAND_COLORS.PANEL,
    '@media (max-width: 760px)': {
      gap: 8,
      padding: 12,
      borderRadius: 14,
    },
  },
  drawerSectionTitle: {
    fontWeight: 800,
    color: BRAND_COLORS.PRIMARY,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 'clamp(0.78rem, 0.88vw, 0.9rem)',
  },
  drawerSectionText: {
    color: BRAND_COLORS.MUTED,
    fontSize: 'clamp(0.72rem, 0.82vw, 0.82rem)',
    lineHeight: 1.45,
  },
  drawerFieldLabel: {
    color: BRAND_COLORS.TEXT,
    fontWeight: 700,
    fontSize: 'clamp(0.68rem, 0.75vw, 0.76rem)',
    letterSpacing: 0.2,
  },
  drawerActionRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  drawerFooter: {
    padding: '0 20px 20px',
  },
  patientNavigationPaper: {
    width: 'min(430px, 100vw)',
    borderTopLeftRadius: 24,
    borderBottomLeftRadius: 24,
    overflow: 'hidden',
    '@media (max-width: 760px)': {
      width: '100vw',
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      maxHeight: '88vh',
    },
  },
  patientNavigationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    '@media (max-width: 540px)': {
      gridTemplateColumns: '1fr',
    },
  },
  patientNavigationTile: {
    display: 'grid',
    justifyItems: 'center',
    gap: 8,
    padding: 14,
    borderRadius: 18,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    background: BRAND_COLORS.PANEL,
    textAlign: 'center',
  },
  patientNavigationIconButton: {
    width: 56,
    height: 56,
    padding: 0,
    borderRadius: 18,
    background: '#fff',
    color: BRAND_COLORS.PRIMARY,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    boxShadow: '0 8px 18px rgba(0, 45, 85, 0.08)',
    '&:hover': {
      background: BRAND_COLORS.PRIMARY,
      color: '#fff',
      borderColor: BRAND_COLORS.PRIMARY,
    },
  },
  patientNavigationIconButtonActive: {
    background: BRAND_COLORS.PRIMARY,
    color: '#fff',
    borderColor: BRAND_COLORS.PRIMARY,
    boxShadow: '0 10px 18px rgba(0, 45, 85, 0.16)',
  },
  patientNavigationTileLabel: {
    color: BRAND_COLORS.PRIMARY,
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  patientNavigationTileHint: {
    color: BRAND_COLORS.MUTED,
    fontSize: 12,
    lineHeight: 1.35,
  },
  blurSlider: {
    width: '100%',
    marginTop: 6,
  },
  blurPreviewNote: {
    fontSize: 12,
    color: BRAND_COLORS.MUTED,
    lineHeight: 1.5,
  },
  controlStack: {
    position: 'absolute',
    zIndex: 2,
    left: '50%',
    bottom: 18,
    transform: 'translateX(-50%)',
    display: 'grid',
    gap: 10,
    width: 'min(736px, calc(100% - 36px))',
    '@media (max-width: 1200px)': {
      bottom: 12,
      gap: 6,
      width: 'min(590px, calc(100% - 24px))',
    },
    '@media (max-width: 760px)': {
      bottom: 8,
      width: 'calc(100% - 16px)',
    },
  },
  devicePair: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 'clamp(6px, 0.8vw, 10px)',
    width: '100%',
    alignItems: 'stretch',
    justifyItems: 'stretch',
    '@media (max-width: 1200px)': {
      gap: 'clamp(4px, 0.5vw, 6px)',
    },
    '@media (max-width: 560px)': {
      gridTemplateColumns: '1fr',
    },
  },
  deviceRow: {
    display: 'grid',
    gridTemplateColumns: '32px minmax(0, 2.6fr) minmax(96px, 0.75fr)',
    gap: 8,
    alignItems: 'stretch',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: '27px minmax(0, 2.3fr) minmax(80px, 0.68fr)',
      gap: 5,
    },
    '@media (max-width: 560px)': {
      gridTemplateColumns: '27px minmax(0, 1fr) auto',
      gap: 4,
    },
  },
  audioIconCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'clamp(26px, 2.3vw, 32px)',
    height: 'clamp(26px, 2.3vw, 32px)',
    borderRadius: 'clamp(8px, 0.8vw, 10px)',
    background: 'rgba(251, 116, 26, 0.08)',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    '@media (max-width: 1200px)': {
      width: 'clamp(22px, 1.8vw, 24px)',
      height: 'clamp(22px, 1.8vw, 24px)',
      borderRadius: 'clamp(6px, 0.6vw, 7px)',
      '& $volumeIcon': {
        transform: 'scale(0.72)',
      },
    },
  },
  volumeIcon: {
    width: 'clamp(15px, 1.2vw, 19px)',
    height: 'clamp(14px, 1vw, 17px)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 2,
  },
  volumeColumn: {
    width: 3,
    borderRadius: 999,
    background: BRAND_COLORS.MUTED,
    transformOrigin: 'bottom center',
    transition: 'all 140ms ease',
  },
  volumeColumnActive: {
    background: BRAND_COLORS.ACCENT,
    animation: '$volumeWave 1.05s ease-in-out infinite',
  },
  volumeColumnTesting: {
    background: BRAND_COLORS.PRIMARY,
    animation: '$volumeWave 0.72s ease-in-out infinite',
  },
  volumeColumnLow: {
    height: 7,
    animationDelay: '0ms',
  },
  volumeColumnMid: {
    height: 12,
    animationDelay: '90ms',
  },
  volumeColumnHigh: {
    height: 17,
    animationDelay: '180ms',
  },
  '@keyframes volumeWave': {
    '0%': { transform: 'scaleY(0.72)' },
    '50%': { transform: 'scaleY(1.18)' },
    '100%': { transform: 'scaleY(0.72)' },
  },
  deviceActionCell: {
    display: 'flex',
    alignItems: 'stretch',
    height: 'clamp(26px, 2.2vw, 32px)',
    '@media (max-width: 1200px)': {
      height: 'clamp(22px, 1.8vw, 24px)',
    },
  },
  deviceActionButton: {
    width: '100%',
    height: 'clamp(26px, 2.2vw, 32px)',
    minHeight: 'clamp(26px, 2.2vw, 32px)',
    padding: '0 8px',
    lineHeight: 1,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&$cancelActionButton': {
      minHeight: 'clamp(26px, 2.2vw, 32px)',
      borderRadius: 14,
    },
    '@media (max-width: 1200px)': {
      height: 'clamp(24px, 1.8vw, 24px)',
      minHeight: 'clamp(24px, 1.8vw, 24px)',
      padding: '0 6px',
      fontSize: 'clamp(0.58rem, 0.66vw, 0.64rem)',
      '&$cancelActionButton': {
        minHeight: 'clamp(24px, 1.8vw, 24px)',
        borderRadius: 10,
      },
    },
  },
  confirmActionButton: {
    width: '100%',
    minHeight: 42,
    borderRadius: 18,
    backgroundColor: BRAND_COLORS.PRIMARY,
    color: '#FFFFFF',
    border: 'none',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#e6eaf0',
      color: '#64748B',
      border: '1px solid #cfd7e3',
      opacity: 1,
      boxShadow: 'none',
    },
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  cancelActionButton: {
    width: '100%',
    minHeight: 40,
    borderRadius: 18,
    backgroundColor: '#e6eaf0',
    color: BRAND_COLORS.PRIMARY,
    border: '2px solid transparent',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '1px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  joinCallButton: {
    width: '100%',
    minHeight: 'clamp(30px, 2.6vw, 38px)',
    fontSize: 'clamp(0.7rem, 0.8vw, 0.8rem)',
    boxShadow: '0 10px 24px rgba(0, 45, 85, 0.28)',
    position: 'relative',
    zIndex: 3,
    '@media (max-width: 1200px)': {
      minHeight: 'clamp(26px, 2vw, 30px)',
      fontSize: 'clamp(0.62rem, 0.7vw, 0.66rem)',
    },
  },
  videoPreviewPanel: {
    borderRadius: 24,
    overflow: 'hidden',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    background: '#002D55',
    height: '100%',
    minHeight: 0,
    aspectRatio: 'auto',
    position: 'absolute',
    inset: 0,
    width: '100%',
    '@media (max-width: 1200px)': {
      width: '100%',
      minHeight: 0,
      borderRadius: 0,
    },
    '@media (max-width: 760px)': {
      width: '100%',
      minHeight: 0,
      borderRadius: 0,
    },
  },
  previewVideo: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
    background: '#000',
  },
  previewPlaceholder: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.85)',
  },
  previewOverlay: {
    position: 'absolute',
    left: 14,
    right: 14,
    bottom: 14,
    borderRadius: 18,
    padding: '8px 10px',
    background: 'rgba(255,255,255,0.94)',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    backdropFilter: 'blur(12px)',
    display: 'grid',
    gap: 8,
    '@media (max-width: 1200px)': {
      left: 10,
      right: 10,
      bottom: 10,
      borderRadius: 14,
      padding: '6px 8px',
      gap: 6,
    },
  },
  videoStatusBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 2,
    pointerEvents: 'none',
    '@media (max-width: 1200px)': {
      top: 8,
      left: 8,
    },
  },
  videoStatusChip: {
    background: BRAND_COLORS.SUCCESS,
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.14)',
    fontWeight: 800,
    '@media (max-width: 1200px)': {
      height: 24,
      fontSize: '0.68rem',
    },
  },
  deviceCheckCard: {
    borderRadius: 24,
    padding: 'clamp(7px, 0.8vw, 11px)',
    background: 'rgba(255, 255, 255, 0.94)',
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    display: 'grid',
    gap: 'clamp(5px, 0.7vw, 8px)',
    boxShadow: '0 8px 20px rgba(0, 45, 85, 0.18)',
    backdropFilter: 'blur(10px)',
    '@media (max-width: 1200px)': {
      gap: 'clamp(4px, 0.5vw, 6px)',
      padding: 'clamp(5px, 0.6vw, 7px)',
      borderRadius: 12,
    },
  },
  deviceCheckLabel: {
    display: 'none',
  },
  deviceCheckValue: {
    color: BRAND_COLORS.TEXT,
    lineHeight: 1.45,
    fontSize: 'clamp(0.72rem, 0.8vw, 0.82rem)',
    '@media (max-width: 1200px)': {
      fontSize: '0.68rem',
      lineHeight: 1.3,
    },
  },
  deviceCheckButtonRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  selectField: {
    width: '100%',
    color: BRAND_COLORS.PRIMARY,
    fontSize: 14,
    outline: 'none',
  },
  deviceSelectField: {
    height: 'clamp(26px, 2.2vw, 32px)',
    background: '#FFFFFF',
    color: BRAND_COLORS.TEXT,
    fontWeight: 500,
    fontSize: 'clamp(0.68rem, 0.92vw, 0.90rem)',
    border: '1px solid #ccc',
    borderRadius: 14,
    padding: '0',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    '@media (max-width: 1200px)': {
      height: 'clamp(24px, 2vw, 27px)',
      fontSize: 'clamp(0.64rem, 0.72vw, 0.68rem)',
      borderRadius: 10,
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
      },
    },
    '& .MuiOutlinedInput-root': {
      height: '100%',
      borderRadius: 14,
      backgroundColor: '#fff',
      '& fieldset': {
        borderColor: '#ccc',
      },
      '&:hover fieldset': {
        borderColor: BRAND_COLORS.ACCENT,
      },
      '&.Mui-focused fieldset': {
        borderColor: BRAND_COLORS.ACCENT,
      },
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      padding: 'clamp(0px, 0.29vw, 0px) clamp(12px, 0.15vw, 24px) !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      background: 'transparent',
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#fff',
      overflow: 'hidden',
      color: BRAND_COLORS.TEXT,
      '& fieldset': {
        borderColor: BRAND_COLORS.BORDER,
      },
      '&:hover fieldset': {
        borderColor: BRAND_COLORS.ACCENT,
      },
      '&.Mui-focused fieldset': {
        borderColor: BRAND_COLORS.ACCENT,
      },
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
      borderRadius: 14,
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink:not(.Mui-focused)': {
      color: BRAND_COLORS.PRIMARY,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: BRAND_COLORS.PRIMARY,
    },
  },
  overlayActions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    paddingTop: 4,
  },
  meterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  meterBar: {
    flex: 1,
    height: 8,
    borderRadius: 999,
    background: BRAND_COLORS.PANEL_ALT,
    overflow: 'hidden',
  },
  meterFill: {
    height: '100%',
    borderRadius: 999,
    background: `linear-gradient(90deg, ${BRAND_COLORS.ACCENT}, ${BRAND_COLORS.PRIMARY})`,
    transition: 'width 120ms linear',
  },
  meterText: {
    color: BRAND_COLORS.MUTED,
    fontSize: 12,
    fontWeight: 700,
    minWidth: 92,
    textAlign: 'right',
  },
  deviceChecklist: {
    display: 'none',
  },
  continueButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 800,
    padding: '9px 18px',
    background: BRAND_COLORS.ACCENT,
    color: '#fff',
    '&:hover': {
      background: '#e86613',
    },
  },
  continueButtonPending: {
    background: BRAND_COLORS.PANEL_ALT,
    color: BRAND_COLORS.PRIMARY,
    border: `1px solid ${BRAND_COLORS.BORDER}`,
    '&:hover': {
      background: BRAND_COLORS.PANEL_ALT,
    },
  },
  continueButtonReady: {
    background: BRAND_COLORS.SUCCESS,
    color: '#fff',
    '&:hover': {
      background: '#3f8f41',
    },
  },
  secondaryButton: {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 800,
    padding: '9px 18px',
    color: BRAND_COLORS.PRIMARY,
    borderColor: BRAND_COLORS.PRIMARY,
    '&:hover': {
      borderColor: BRAND_COLORS.ACCENT,
      background: 'rgba(251, 116, 26, 0.06)',
    },
  },
  preflightHint: {
    color: BRAND_COLORS.MUTED,
    fontSize: 12.5,
    lineHeight: 1.5,
  },
  roomState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 28,
    textAlign: 'center',
  },
  roomStateTitle: {
    color: BRAND_COLORS.PRIMARY,
    fontWeight: 800,
    fontSize: 18,
  },
  roomStateBody: {
    color: BRAND_COLORS.MUTED,
    maxWidth: 500,
    lineHeight: 1.6,
  },
  summaryPillRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  loadingShell: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    minHeight: 0,
    padding: 28,
    background: '#fff',
  },
  errorShell: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 28,
    textAlign: 'center',
    background: '#fff',
  },
  errorTitle: {
    color: BRAND_COLORS.ACCENT,
    fontWeight: 800,
    fontSize: 18,
  },
  errorText: {
    color: BRAND_COLORS.TEXT,
    maxWidth: 520,
    lineHeight: 1.6,
  },
}));

const getBrowserName = () => {
  if (typeof navigator === 'undefined') {
    return 'Browser';
  }

  const ua = navigator.userAgent || '';
  if (/firefox/i.test(ua)) {
    return 'Firefox';
  }

  if (/edg/i.test(ua)) {
    return 'Edge';
  }

  if (/chrome|chromium|crios/i.test(ua) && !/edg/i.test(ua)) {
    return 'Chrome';
  }

  if (/safari/i.test(ua) && !/chrome|chromium|crios/i.test(ua)) {
    return 'Safari';
  }

  return 'Browser';
};

const getQuickstartGuide = (browserName) => {
  const commonSteps = [
    {
      title: 'Keep this page open',
      body: 'Do not reload once you are inside the videocall. A refresh exits the current session and sends you back to the start screen.',
    },
    {
      title: 'Allow camera and microphone',
      body: 'Click allow when the browser asks for camera and microphone access. If you blocked it before, open the site permissions for this page and set Microphone to Allow.',
    },
    {
      title: 'Select your devices',
      body: 'Use the microphone dropdown to choose the input you want. Select speakers or headphones when the browser supports it. If you do not see the device you want, click test to auto-search. If you still don\'t see it, check that it is connected and recognized by the operating system.',
    },
    {
      title: 'Run the tests',
      body: 'Click Test microphone and Test speakers so the app can confirm both devices before you join.',
    },
  ];

  if (browserName === 'Safari') {
    return {
      badge: 'Safari guide',
      intro: 'Safari needs one user click to unlock the device refresh and speaker check. Use the setup button in this guide, then follow the steps below to get the microphone and speaker controls working without leaving the call.',
      notice: 'Safari still uses the default speaker or headphones in many cases. If the speaker list looks limited, that is expected even after setup.',
      steps: [
        commonSteps[0],
        {
          title: 'Allow microphone access',
          body: 'Use Safari site settings to allow microphone access for this page. If permission was denied earlier, re-enable it from the address bar site menu.',
        },
        {
          title: 'Start Safari setup',
          body: 'Click Start Safari setup in this guide. That single click runs the permissioned refresh and speaker check from the same user gesture Safari allows.',
        },
        {
          title: 'Use the result',
          body: 'After setup completes, the microphone and speaker controls should already be populated. Only use the Test buttons if Safari still needs a retry.',
        },
      ],
    };
  }

  if (browserName === 'Edge' || browserName === 'Chrome') {
    return {
      badge: `${browserName} guide`,
      intro: 'Edge and Chrome expose the broadest device list. If you allowed permission already, the lists should populate after the quick device check.',
      notice: 'If the browser still looks blocked, open the lock icon beside the address bar and set camera and microphone to Allow.',
      steps: [
        commonSteps[0],
        {
          title: 'Open site permissions if needed',
          body: 'Use the lock icon in the address bar to confirm camera and microphone are allowed for this site. You usually do not need to refresh after that.',
        },
        commonSteps[2],
        commonSteps[3],
      ],
    };
  }

  if (browserName === 'Firefox') {
    return {
      badge: 'Firefox guide',
      intro: 'Firefox can keep permissions more tightly scoped, so the quickest path is to allow access and then re-run the device check from this screen.',
      notice: 'If Firefox still does not show the devices, reopen the site permissions for this page and confirm microphone access is not blocked.',
      steps: [
        commonSteps[0],
        {
          title: 'Allow access to microphone and camera',
          body: 'When Firefox asks, allow camera and microphone for this site. If the prompt does not appear, use the site permissions panel to reset the block.',
        },
        commonSteps[2],
        commonSteps[3],
      ],
    };
  }

  return {
    badge: 'Quickstart guide',
    intro: 'Follow these steps to get camera, microphone, and speakers ready without leaving the videocall.',
    notice: 'If a permission was denied earlier, reset that site permission first. Then come back here and run the tests again.',
    steps: commonSteps,
  };
};

const formatAppointmentRange = (appointment) => {
  if (!appointment?.start_datetime) {
    return '';
  }

  const start = new Date(appointment.start_datetime);
  if (Number.isNaN(start.getTime())) {
    return '';
  }

  const end = appointment?.end_datetime ? new Date(appointment.end_datetime) : null;
  const dateFormatter = new Intl.DateTimeFormat([], { weekday: 'short', month: 'short', day: 'numeric' });
  const timeFormatter = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' });
  const dateLabel = dateFormatter.format(start);
  const startLabel = timeFormatter.format(start);

  if (end && !Number.isNaN(end.getTime())) {
    const endLabel = timeFormatter.format(end);
    return `${dateLabel} · ${startLabel} – ${endLabel}`;
  }

  return `${dateLabel} · ${startLabel}`;
};

const getAppointmentSubtitle = (appointment, appointmentTitle = '') => {
  const parts = [];
  const normalizedPatientName = (appointment?.patient_name || '').trim().toLowerCase();
  const normalizedTitle = (appointmentTitle || '').trim().toLowerCase();
  const showPatientName = normalizedPatientName && !normalizedTitle.includes(normalizedPatientName);

  if (appointment?.patient_name && showPatientName) {
    parts.push(appointment.patient_name);
  }

  const serviceLabel = appointment?.service_name || appointment?.appointment_type;
  if (serviceLabel) {
    parts.push(serviceLabel);
  }

  const rangeLabel = formatAppointmentRange(appointment);
  if (rangeLabel) {
    parts.push(rangeLabel);
  }

  return parts.join(' · ');
};

const resolvePatientPhaseProgress = (progressValue) => {
  if (progressValue === null || progressValue === undefined) {
    return { value: null, display: '' };
  }

  if (typeof progressValue === 'number') {
    return { value: progressValue, display: `${progressValue}%` };
  }

  if (typeof progressValue === 'string') {
    const normalizedNumber = Number(progressValue.replace(/%/g, '').trim());
    return {
      value: Number.isFinite(normalizedNumber) ? normalizedNumber : null,
      display: progressValue,
    };
  }

  if (typeof progressValue === 'object') {
    const resolvedValue = progressValue.percent
      ?? progressValue.value
      ?? progressValue.anchor_actual_value
      ?? null;
    const normalizedNumber = Number(resolvedValue);
    const display = progressValue.anchor_actual_display
      || progressValue.anchor_summary
      || progressValue.anchor_threshold_display
      || (Number.isFinite(normalizedNumber) ? `${normalizedNumber}%` : '');

    return {
      value: Number.isFinite(normalizedNumber) ? normalizedNumber : null,
      display,
    };
  }

  return { value: null, display: String(progressValue) };
};

const PATIENT_WORKSPACE_SECTIONS = [
  { key: 'progress', label: 'Progress', hint: 'Graphs and milestone tracking' },
  { key: 'program', label: 'Program', hint: 'Current exercises and phase overview' },
  { key: 'feedback', label: 'Feedback', hint: 'Latest completed sessions' },
  { key: 'messages', label: 'Messages', hint: 'Open your chat thread' },
  { key: 'billing', label: 'Insurance & Billing', hint: 'Account, insurance, and payments' },
  { key: 'condition', label: 'My condition & symptoms', hint: 'Read your pain and symptom guidance' },
];

const getPatientWorkspaceUrl = (sectionKey) => {
  const appendEmbeddedFlag = (url) => (url.includes('?') ? `${url}&embedded=1` : `${url}?embedded=1`);

  switch (sectionKey) {
    case 'program':
      return appendEmbeddedFlag('/physiotherapeutic_plan');
    case 'feedback':
      return appendEmbeddedFlag('/patient_feedback');
    case 'messages':
      return appendEmbeddedFlag('/patient_messages');
    case 'billing':
      return appendEmbeddedFlag('/myaccount?tab=payment');
    case 'condition':
      return appendEmbeddedFlag('/learn_about_your_pain');
    case 'progress':
    default:
      return appendEmbeddedFlag('/overview_of_journey');
  }
};

const getDeviceOptionLabel = (device, index, fallbackLabel) => device.label || `${fallbackLabel} ${index + 1}`;

const RemoteParticipantVideo = ({ participant, classes }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !participant) {
      return undefined;
    }

    const publications = typeof participant.getTrackPublications === 'function'
      ? participant.getTrackPublications()
      : [];
    const videoPublication = publications.find((publication) => publication.kind === 'video' && publication.isSubscribed && publication.track);
    const track = videoPublication?.track || null;

    if (!track) {
      videoElement.srcObject = null;
      return undefined;
    }

    track.attach(videoElement);
    return () => {
      try {
        track.detach(videoElement);
      } catch (error) {
        void error;
      }
    };
  }, [participant, participant?.sid, participant?.identity, participant?.name]);

  return (
    <video
      ref={videoRef}
      className={classes.workspaceCallFrame}
      autoPlay
      playsInline
      muted
    />
  );
};

const WorkspacePiP = ({ classes, blurLevel, mediaStream, participant }) => {
  const [pipSize, setPipSize] = useState({ width: 240, height: 170 });
  const dragStateRef = useRef(null);

  useEffect(() => {
    setPipSize({ width: 240, height: 170 });
  }, [participant?.sid, participant?.identity]);

  useEffect(() => () => {
    dragStateRef.current = null;
  }, []);

  const startResize = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: pipSize.width,
      startHeight: pipSize.height,
    };

    const onPointerMove = (moveEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || moveEvent.pointerId !== dragState.pointerId) {
        return;
      }

      const nextWidth = Math.max(150, Math.min(420, dragState.startWidth + (moveEvent.clientX - dragState.startX)));
      const nextHeight = Math.max(110, Math.min(320, dragState.startHeight - (moveEvent.clientY - dragState.startY)));
      setPipSize({ width: nextWidth, height: nextHeight });
    };

    const stopResize = (upEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || upEvent.pointerId !== dragState.pointerId) {
        return;
      }

      dragStateRef.current = null;
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', stopResize, true);
      window.removeEventListener('pointercancel', stopResize, true);
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', stopResize, true);
    window.addEventListener('pointercancel', stopResize, true);

    if (event.currentTarget?.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        void error;
      }
    }
  }, [pipSize.height, pipSize.width]);

  return (
    <Box className={classes.workspacePiP} style={{ width: pipSize.width, height: pipSize.height }}>
      <Box
        className={classes.workspacePiPResizeHandle}
        onPointerDown={startResize}
        aria-label="Resize picture-in-picture"
        role="slider"
        tabIndex={0}
      >
        <ZoomOutMapIcon style={{ fontSize: 12 }} />
      </Box>
      <Box className={classes.workspacePiPLabel}>You</Box>
      <Box className={classes.workspacePiPBody}>
        {mediaStream ? (
          <BackgroundAwareSelfView classes={classes} mediaStream={mediaStream} blurLevel={blurLevel} />
        ) : (
          <Box className={classes.workspacePiPPlaceholder}>
            <Typography style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>
              Camera connecting
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const BackgroundAwareSelfView = ({ classes, mediaStream, blurLevel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const blurCanvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const segmenterRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [segmenterLoadError, setSegmenterLoadError] = useState('');

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !mediaStream) {
      return undefined;
    }

    videoElement.srcObject = mediaStream;
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.autoplay = true;
    void videoElement.play().catch(() => {});

    return () => {
      if (videoElement.srcObject === mediaStream) {
        videoElement.srcObject = null;
      }
    };
  }, [mediaStream]);

  useEffect(() => {
    if (blurLevel <= 0) {
      setSegmenterLoadError('');
      return undefined;
    }

    let cancelled = false;

    const loadSegmenter = async () => {
      if (segmenterRef.current) {
        return;
      }

      try {
        const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);
        if (cancelled) {
          return;
        }

        segmenterRef.current = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: SELFIE_SEGMENTER_MODEL_URL,
          },
          runningMode: 'VIDEO',
          outputConfidenceMasks: true,
          outputCategoryMask: false,
        });
        setSegmenterLoadError('');
      } catch (segmenterError) {
        setSegmenterLoadError(segmenterError?.message || 'Background blur could not be initialized.');
      }
    };

    void loadSegmenter();

    return () => {
      cancelled = true;
    };
  }, [blurLevel]);

  useEffect(() => {
    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;
    if (!videoElement || !canvasElement || !mediaStream) {
      return undefined;
    }

    const mainContext = canvasElement.getContext('2d');
    if (!mainContext) {
      return undefined;
    }

    const blurCanvas = blurCanvasRef.current || document.createElement('canvas');
    blurCanvasRef.current = blurCanvas;
    const blurContext = blurCanvas.getContext('2d');
    if (!blurContext) {
      return undefined;
    }

    const maskCanvas = maskCanvasRef.current || document.createElement('canvas');
    maskCanvasRef.current = maskCanvas;
    const maskContext = maskCanvas.getContext('2d');
    if (!maskContext) {
      return undefined;
    }

    let cancelled = false;

    const drawFrame = () => {
      if (cancelled) {
        return;
      }

      if (!videoElement.videoWidth || !videoElement.videoHeight || videoElement.readyState < 2) {
        animationFrameRef.current = window.requestAnimationFrame(drawFrame);
        return;
      }

      const width = videoElement.videoWidth;
      const height = videoElement.videoHeight;
      if (canvasElement.width !== width) {
        canvasElement.width = width;
      }
      if (canvasElement.height !== height) {
        canvasElement.height = height;
      }
      if (blurCanvas.width !== width) {
        blurCanvas.width = width;
      }
      if (blurCanvas.height !== height) {
        blurCanvas.height = height;
      }
      if (maskCanvas.width !== width) {
        maskCanvas.width = width;
      }
      if (maskCanvas.height !== height) {
        maskCanvas.height = height;
      }

      mainContext.clearRect(0, 0, width, height);
      mainContext.drawImage(videoElement, 0, 0, width, height);

      if (blurLevel > 0 && segmenterRef.current) {
        try {
          segmenterRef.current.segmentForVideo(videoElement, performance.now(), (segmentationResult) => {
            const confidenceMask = segmentationResult.confidenceMasks?.[0];
            const maskValues = confidenceMask?.getAsFloat32Array();

            if (!maskValues || maskValues.length !== width * height) {
              return;
            }

            const maskImageData = maskContext.createImageData(width, height);
            for (let index = 0; index < maskValues.length; index += 1) {
              const alpha = Math.max(0, Math.min(255, Math.round((1 - maskValues[index]) * 255)));
              const pixelOffset = index * 4;
              maskImageData.data[pixelOffset] = 255;
              maskImageData.data[pixelOffset + 1] = 255;
              maskImageData.data[pixelOffset + 2] = 255;
              maskImageData.data[pixelOffset + 3] = alpha;
            }

            maskContext.putImageData(maskImageData, 0, 0);
            blurContext.clearRect(0, 0, width, height);
            blurContext.filter = `blur(${Math.max(blurLevel / 12, 0)}px) saturate(0.95)`;
            blurContext.drawImage(videoElement, 0, 0, width, height);
            blurContext.filter = 'none';
            blurContext.globalCompositeOperation = 'destination-in';
            blurContext.drawImage(maskCanvas, 0, 0, width, height);
            blurContext.globalCompositeOperation = 'source-over';
            mainContext.drawImage(blurCanvas, 0, 0, width, height);
          });
        } catch (renderError) {
          setSegmenterLoadError(renderError?.message || 'Background blur could not be rendered.');
        }
      }

      animationFrameRef.current = window.requestAnimationFrame(drawFrame);
    };

    drawFrame();

    return () => {
      cancelled = true;
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [blurLevel, mediaStream]);

  return (
    <Box className={classes.selfViewStage}>
      <video ref={videoRef} className={classes.selfViewVideoSource} playsInline muted autoPlay hidden />
      <canvas ref={canvasRef} className={classes.selfViewCanvas} />
      {blurLevel > 0 && segmenterLoadError ? (
        <Box className={classes.selfViewFallbackNotice}>
          <Typography style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>
            {segmenterLoadError}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

const VideoCallQuickstartDialog = ({ open, onClose, classes, browserName, onStartSafariSetup }) => {
  const guide = useMemo(() => getQuickstartGuide(browserName), [browserName]);
  const isSafariGuide = browserName === 'Safari';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: classes.quickstartPaper }}
    >
      <Box className={classes.quickstartDialog}>
        <Box className={classes.quickstartHeader}>
          <Box className={classes.quickstartTitleRow}>
            <Typography className={classes.quickstartTitle}>Quickstart guide</Typography>
            <Chip size="small" label={guide.badge} className={classes.quickstartBrowserPill} />
          </Box>
          <Typography className={classes.quickstartIntro}>{guide.intro}</Typography>
        </Box>

        <Box className={classes.quickstartBody}>
          <Box className={classes.quickstartNotice}>{guide.notice}</Box>

          <Box className={classes.quickstartStepList}>
            {guide.steps.map((step, index) => (
              <Box key={step.title} className={classes.quickstartStepRow}>
                <Box className={classes.quickstartStepNumber}>{index + 1}</Box>
                <Box className={classes.quickstartStepCard}>
                  <Typography className={classes.quickstartStepTitle}>{step.title}</Typography>
                  <Typography className={classes.quickstartStepBody}>{step.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={classes.quickstartFooter}>
          {isSafariGuide ? (
            <Button variant="outlined" className={classes.quickstartButton} onClick={onStartSafariSetup} style={{ marginRight: 10 }}>
              Start Safari setup
            </Button>
          ) : null}
          <Button variant="contained" className={classes.confirmActionButton} onClick={onClose}>
            Got it
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

const VideoCallActionToolbar = ({
  classes,
  isMobile,
  blurLevel,
  screenShareState,
  onOpenSettings,
  onOpenQuickstart,
  onOpenBlur,
  onToggleScreenShare,
}) => {
  const blurActive = blurLevel > 0;
  const screenShareActive = screenShareState !== 'idle';

  const buttonContent = (icon, label, hint) => (
    <>
      <Box className={classes.toolbarButtonIcon}>{icon}</Box>
      <Box style={{ display: 'grid', justifyItems: 'start', textAlign: 'left' }}>
        <Typography className={classes.actionToolbarButtonLabel}>{label}</Typography>
        {hint ? <Typography className={classes.actionToolbarButtonHint}>{hint}</Typography> : null}
      </Box>
    </>
  );

  return (
    <Box className={classes.toolbarWrap}>
      <Box className={classes.actionToolbar}>
        <Tooltip title="Device settings">
          <Button
            variant="outlined"
            className={`${classes.actionToolbarButton}`}
            onClick={onOpenSettings}
          >
            {buttonContent(<SettingsIcon style={{marginRight: 16}} />, 'Settings', isMobile ? '' : 'Mic and speakers')}
          </Button>
        </Tooltip>

        <Tooltip title="Open the browser setup guide">
          <Button
            variant="outlined"
            className={`${classes.actionToolbarButton}`}
            onClick={onOpenQuickstart}
          >
            {buttonContent(<VideoCallIcon style={{marginRight: 16}} />, 'Quickstart', isMobile ? '' : 'Setup guide')}
          </Button>
        </Tooltip>

        <Tooltip title={blurActive ? `Background blur ${blurLevel}%` : 'Background blur'}>
          <Button
            variant="outlined"
            className={`${classes.actionToolbarButton} ${blurActive ? classes.actionToolbarButtonActive : ''}`}
            onClick={onOpenBlur}
          >
            {buttonContent(<BlurOnIcon style={{marginRight: 16}} />, 'Background', isMobile ? '' : `${blurLevel}% blur`)}
          </Button>
        </Tooltip>

        <Tooltip title={screenShareActive ? 'Stop screen sharing' : 'Share screen'}>
          <Button
            variant="outlined"
            className={`${classes.actionToolbarButton} ${screenShareActive ? classes.actionToolbarButtonActive : ''}`}
            onClick={onToggleScreenShare}
          >
            {buttonContent(screenShareActive ? <StopScreenShareIcon style={{marginRight: 16}} /> : <ScreenShareIcon style={{marginRight: 16}} />, 'Share screen', isMobile ? '' : (screenShareActive ? 'Sharing now' : 'Select a screen'))}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

const VideoCallSettingsDrawer = ({
  open,
  onClose,
  classes,
  isMobile,
  supportsAudioOutputSelection,
  selectedAudioInputId,
  setSelectedAudioInputId,
  selectedAudioOutputId,
  setSelectedAudioOutputId,
  availableAudioInputs,
  availableAudioOutputs,
  onTestMicrophone,
  onTestSpeakers,
  voiceInputConfirmed,
  soundOutputConfirmed,
  preflightStatus,
  speakerTestStatus,
  blurLevel,
  setBlurLevel,
  screenShareState,
  screenShareMessage,
  shareScreenHint,
  onToggleScreenShare,
  onOpenQuickstart,
}) => {
  const screenShareActive = screenShareState !== 'idle';

  const drawerContent = (
    <>
      <Box className={classes.drawerHeader}>
        <Typography className={classes.drawerTitle}>Call controls</Typography>
        <Typography className={classes.drawerSubtitle}>
          Adjust your microphone, speakers, background effect, and screen sharing from one place.
        </Typography>
      </Box>

      <Box className={classes.drawerBody}>
        <Box className={classes.drawerSection}>
          <Typography className={classes.drawerSectionTitle}>
            <SettingsIcon fontSize="small" /> Device settings
          </Typography>
          <Typography className={classes.drawerSectionText}>
            Pick the microphone and output device you want to use in this call.
          </Typography>

          <Typography className={classes.drawerFieldLabel}>Microphone</Typography>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            select
            SelectProps={{ MenuProps: AUDIO_SELECT_MENU_PROPS, displayEmpty: true }}
            className={`${classes.orangeFocused} ${classes.deviceSelectField}`}
            value={selectedAudioInputId}
            onChange={(event) => {
              const nextAudioInputId = event.target.value;
              setSelectedAudioInputId(nextAudioInputId);
              writeStoredDeviceId(VIDEO_CALL_MICROPHONE_STORAGE_KEY, nextAudioInputId);
            }}
          >
            <MenuItem value="">Default microphone</MenuItem>
            {availableAudioInputs.map((device, index) => (
              <MenuItem key={device.deviceId || `mic-${index}`} value={device.deviceId}>
                {getDeviceOptionLabel(device, index, 'Microphone')}
              </MenuItem>
            ))}
          </TextField>

          <Typography className={classes.drawerFieldLabel}>Speakers / headphones</Typography>
          {!supportsAudioOutputSelection ? (
            <Typography className={classes.drawerSectionText} style={{ marginBottom: 8 }}>
              Safari does not support selecting a specific speaker or headphone output in this call UI. The default output will be used.
            </Typography>
          ) : null}
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            select
            SelectProps={{ MenuProps: AUDIO_SELECT_MENU_PROPS, displayEmpty: true }}
            className={`${classes.orangeFocused} ${classes.deviceSelectField}`}
            value={selectedAudioOutputId}
            disabled={!supportsAudioOutputSelection}
            onChange={(event) => {
              const nextAudioOutputId = event.target.value;
              setSelectedAudioOutputId(nextAudioOutputId);
              writeStoredDeviceId(VIDEO_CALL_SPEAKER_STORAGE_KEY, nextAudioOutputId);
            }}
          >
            <MenuItem value="">Default speakers</MenuItem>
            {availableAudioOutputs.map((device, index) => (
              <MenuItem key={device.deviceId || `speaker-${index}`} value={device.deviceId}>
                {getDeviceOptionLabel(device, index, 'Speaker')}
              </MenuItem>
            ))}
          </TextField>

          <Box className={classes.drawerActionRow}>
            <Button
              variant="outlined"
              className={classes.cancelActionButton}
              onClick={onTestMicrophone}
              disabled={preflightStatus === 'checking'}
            >
              {voiceInputConfirmed ? 'Microphone ready' : 'Test microphone'}
            </Button>
            <Button
              variant="outlined"
              className={classes.cancelActionButton}
              onClick={onTestSpeakers}
              disabled={speakerTestStatus === 'testing' || preflightStatus === 'checking'}
            >
              {soundOutputConfirmed ? 'Speakers ready' : 'Test speakers'}
            </Button>
          </Box>
        </Box>

        <Box className={classes.drawerSection}>
          <Typography className={classes.drawerSectionTitle}>
            <BlurOnIcon fontSize="small" /> Background blur
          </Typography>
          <Typography className={classes.drawerSectionText}>
            Use a slider to soften the background effect. First release is a toggle/slider control, not a full segmentation engine.
          </Typography>

          <Slider
            className={classes.blurSlider}
            value={blurLevel}
            min={0}
            max={100}
            step={5}
            onChange={(_event, value) => setBlurLevel(Array.isArray(value) ? value[0] : value)}
            aria-label="Background blur level"
          />
          <Typography className={classes.blurPreviewNote}>
            Blur intensity: {blurLevel}%
          </Typography>
        </Box>

        <Box className={classes.drawerSection}>
          <Typography className={classes.drawerSectionTitle}>
            <ScreenShareIcon fontSize="small" /> Screen sharing
          </Typography>
          <Typography className={classes.drawerSectionText}>
            Share a tab, window, or full screen. You can start sharing before joining and it will publish when the room connects.
          </Typography>
          <Typography className={classes.drawerFieldLabel}>
            Status: {screenShareState === 'live' ? 'Sharing now' : screenShareState === 'pending' ? 'Ready to publish' : 'Off'}
          </Typography>
          {screenShareMessage ? (
            <Typography className={classes.blurPreviewNote}>
              {screenShareMessage}
            </Typography>
          ) : null}
          {shareScreenHint ? (
            <Typography className={classes.blurPreviewNote}>
              {shareScreenHint}
            </Typography>
          ) : null}
          <Button
            variant="outlined"
            className={classes.cancelActionButton}
            onClick={onToggleScreenShare}
          >
            {screenShareActive ? 'Stop screen sharing' : 'Share screen'}
          </Button>
        </Box>

        <Box className={classes.drawerSection}>
          <Typography className={classes.drawerSectionTitle}>
            <VideoCallIcon fontSize="small" /> Quickstart
          </Typography>
          <Typography className={classes.drawerSectionText}>
            Review the browser-specific setup guide before joining the call.
          </Typography>
          <Button
            variant="outlined"
            className={classes.cancelActionButton}
            onClick={onOpenQuickstart}
          >
            Open quickstart
          </Button>
        </Box>
      </Box>
    </>
  );

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.drawerPaper }}
      variant="temporary"
    >
      {drawerContent}
      <Box className={classes.drawerFooter}>
        <Button fullWidth variant="contained" className={classes.confirmActionButton} onClick={onClose}>
          Done
        </Button>
      </Box>
    </Drawer>
  );
};

const VideoCallPatientWorkspacePanel = ({
  open,
  onClose,
  classes,
  workspaceVariant = 'clinician',
  authToken,
  treatmentId,
  activeSectionKey = 'feedback',
}) => {
  const [iframeReady, setIframeReady] = useState(false);
  const [patientWorkspaceLoading, setPatientWorkspaceLoading] = useState(false);
  const [patientWorkspaceError, setPatientWorkspaceError] = useState('');
  const [patientWorkspaceData, setPatientWorkspaceData] = useState({
    treatmentId: '',
    treatmentName: '',
    phaseTitle: '',
    phaseProgress: null,
    phaseProgressDisplay: '',
    latestSession: null,
    latestSessions: [],
  });
  const patientWorkspaceUrl = '/admin_dashboard';

  useEffect(() => {
    if (open) {
      setIframeReady(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || workspaceVariant !== 'patient' || !treatmentId || typeof window === 'undefined') {
      return undefined;
    }

    try {
      window.localStorage.setItem('vp_selectedTreatmentId', treatmentId);
    } catch (error) {
      void error;
    }

    return undefined;
  }, [open, treatmentId, workspaceVariant]);

  useEffect(() => {
    if (!open || workspaceVariant !== 'patient') {
      return undefined;
    }

    let cancelled = false;

    const loadPatientWorkspace = async () => {
      const resolvedTreatmentId = treatmentId || (typeof window !== 'undefined' ? window.localStorage.getItem('vp_selectedTreatmentId') : '');
      if (!resolvedTreatmentId) {
        if (!cancelled) {
          setPatientWorkspaceLoading(false);
          setPatientWorkspaceError('Select a treatment plan in the patient dashboard to load the exercise workspace.');
          setPatientWorkspaceData({
            treatmentId: '',
            treatmentName: '',
            phaseTitle: '',
            phaseProgress: null,
            phaseProgressDisplay: '',
            latestSession: null,
            latestSessions: [],
          });
        }
        return;
      }

      setPatientWorkspaceLoading(true);
      setPatientWorkspaceError('');

      try {
        const headers = {
          Authorization: `Token ${authToken}`,
          Accept: 'application/json',
        };

        const [phaseRes, sessionsRes, treatmentsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/treatment/phase/progress/treatment/${resolvedTreatmentId}/`, { headers }),
          fetch(`${BACKEND_URL}/treatment/sessions/all/treatment/${resolvedTreatmentId}/`, { headers }),
          fetch(`${BACKEND_URL}/treatment/treatments/`, { headers }),
        ]);

        const [phaseData, sessionsData, treatmentsData] = await Promise.all([
          phaseRes.ok ? phaseRes.json() : Promise.resolve({}),
          sessionsRes.ok ? sessionsRes.json() : Promise.resolve({}),
          treatmentsRes.ok ? treatmentsRes.json() : Promise.resolve({}),
        ]);

        const latestSessions = Array.isArray(sessionsData?.sessions) ? [...sessionsData.sessions] : [];
        latestSessions.sort((left, right) => {
          const leftTime = new Date(left?.completed_date || 0).getTime();
          const rightTime = new Date(right?.completed_date || 0).getTime();
          return rightTime - leftTime;
        });

        const treatmentList = Array.isArray(treatmentsData)
          ? treatmentsData
          : Array.isArray(treatmentsData?.treatments)
            ? treatmentsData.treatments
            : [];
        const treatmentRecord = treatmentList.find((item) => String(item?.id) === String(resolvedTreatmentId));
        const resolvedPhaseProgress = resolvePatientPhaseProgress(phaseData?.progress);

        if (!cancelled) {
          setPatientWorkspaceData({
            treatmentId: resolvedTreatmentId,
            treatmentName: treatmentRecord?.name || treatmentRecord?.title || treatmentRecord?.diagnosis_name || 'Current exercise program',
            phaseTitle: phaseData?.name || phaseData?.phase_name || '',
            phaseProgress: resolvedPhaseProgress.value,
            phaseProgressDisplay: phaseData?.progress_display || phaseData?.progress_percent || resolvedPhaseProgress.display || '',
            latestSession: latestSessions[0] || null,
            latestSessions,
          });
        }
      } catch (workspaceError) {
        if (!cancelled) {
          setPatientWorkspaceError(workspaceError?.message || 'Unable to load the current exercise workspace.');
        }
      } finally {
        if (!cancelled) {
          setPatientWorkspaceLoading(false);
        }
      }
    };

    void loadPatientWorkspace();

    return () => {
      cancelled = true;
    };
  }, [authToken, open, treatmentId, workspaceVariant]);

  if (!open) {
    return null;
  }

  if (workspaceVariant === 'patient') {
    const patientWorkspaceUrl = getPatientWorkspaceUrl(activeSectionKey);

    return (
      <Box className={classes.workspacePanelPatient}>
        <Box className={classes.patientWorkspaceScroll}>
          <Box className={classes.workspaceFrameShellPatient}>
            {patientWorkspaceLoading ? (
              <Box className={classes.workspaceFrameLoading}>
                <CircularProgress size={28} />
              </Box>
            ) : null}
            {patientWorkspaceError ? (
              <Box className={classes.workspaceFrameLoading} style={{ background: '#fff', color: '#002D55' }}>
                <Typography style={{ padding: 20, textAlign: 'center', color: '#002D55' }}>
                  {patientWorkspaceError}
                </Typography>
              </Box>
            ) : null}
            <iframe
              title={`${PATIENT_WORKSPACE_SECTIONS.find((section) => section.key === activeSectionKey)?.label || 'Patient'} workspace`}
              src={patientWorkspaceUrl}
              className={classes.workspaceFrame}
              onLoad={() => setIframeReady(true)}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.workspacePanel}>
      <Box className={classes.workspaceFrameShell}>
        <Tooltip title="Close workspace">
          <IconButton
            onClick={onClose}
            className={`${classes.headerActionButton} ${classes.workspaceHeaderActionButton}`}
            aria-label="Close workspace"
          >
            <RemoveIcon />
          </IconButton>
        </Tooltip>
        {!iframeReady ? (
          <Box className={classes.workspaceFrameLoading}>
            <CircularProgress size={28} />
          </Box>
        ) : null}
        <iframe
          title="Patient program workspace"
          src={patientWorkspaceUrl}
          className={classes.workspaceFrame}
          onLoad={() => setIframeReady(true)}
        />
      </Box>
    </Box>
  );
};

const ScreenSharePublisherBridge = ({
  screenShareState,
  screenShareStreamRef,
  screenShareTrackRef,
  screenSharePublishedTrackRef,
  onScreenShareLive,
  onScreenShareStopped,
}) => {
  const room = useMaybeRoomContext();

  useEffect(() => {
    if (!room) {
      return undefined;
    }

    let cancelled = false;

    const publishPendingShare = async () => {
      if (screenShareState !== 'pending') {
        return;
      }

      const pendingStream = screenShareStreamRef.current;
      const pendingTrack = pendingStream?.getVideoTracks?.()[0];
      if (!pendingTrack) {
        return;
      }

      if (screenSharePublishedTrackRef.current) {
        return;
      }

      try {
        await room.localParticipant.publishTrack(pendingTrack, {
          source: Track.Source.ScreenShare,
          stream: 'screen-share',
          name: 'screen-share',
          screenShareEncoding: { maxBitrate: 2500000 },
        });
        if (!cancelled) {
          screenSharePublishedTrackRef.current = pendingTrack;
          onScreenShareLive();
        }
      } catch (publishError) {
        if (!cancelled) {
          onScreenShareStopped(publishError?.message || 'Unable to start screen sharing.');
        }
      }
    };

    const stopPublishedShare = async () => {
      if (screenShareState !== 'idle') {
        return;
      }

      const publishedTrack = screenSharePublishedTrackRef.current;
      if (!publishedTrack) {
        return;
      }

      try {
        await room.localParticipant.unpublishTrack(publishedTrack, true);
      } catch (unpublishError) {
        void unpublishError;
      }
      screenSharePublishedTrackRef.current = null;
      onScreenShareStopped('Screen share stopped.');
    };

    void publishPendingShare();
    void stopPublishedShare();

    return () => {
      cancelled = true;
    };
  }, [onScreenShareLive, onScreenShareStopped, room, screenShareState, screenShareStreamRef, screenShareTrackRef, screenSharePublishedTrackRef]);

  return null;
};

const LiveCallStage = ({ classes, mediaStreamRef, blurLevel }) => {
  const remoteParticipants = useRemoteParticipants();
  const primaryRemoteParticipant = remoteParticipants[0] || null;

  return (
    <Box className={classes.workspaceCallStage}>
      {primaryRemoteParticipant ? (
        <RemoteParticipantVideo participant={primaryRemoteParticipant} classes={classes} />
      ) : (
        <Box className={classes.workspaceCallFallback}>
          <Box>
            <Typography className={classes.panelPlaceholderTitle}>
              Waiting for guest
            </Typography>
            <Typography className={classes.panelPlaceholderBody}>
              The invited participant will fill this frame once they connect.
            </Typography>
          </Box>
        </Box>
      )}

      <WorkspacePiP
        classes={classes}
        blurLevel={blurLevel}
        mediaStream={mediaStreamRef.current}
        participant={primaryRemoteParticipant}
      />
    </Box>
  );
};
const VideoCallModal = ({ open, appointment, authToken, onClose, workspaceVariant = 'clinician', patientTreatmentId = '' }) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 760px)');
  const isCompactLayout = useMediaQuery('(max-width: 1200px)');
  const [patientWorkspaceOpen, setPatientWorkspaceOpen] = useState(false);
  const [patientWorkspaceMenuAnchorEl, setPatientWorkspaceMenuAnchorEl] = useState(null);
  const [patientWorkspaceSection, setPatientWorkspaceSection] = useState('feedback');
  const previewVideoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const speakerTestAudioRef = useRef(null);
  const speakerTestContextRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isMountedRef = useRef(false);
  const screenShareStreamRef = useRef(null);
  const screenShareTrackRef = useRef(null);
  const screenSharePublishedTrackRef = useRef(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [videoSession, setVideoSession] = useState(() => appointment?.video_session || null);
  const [elapsedLabel, setElapsedLabel] = useState('00:00');
  const [preflightStatus, setPreflightStatus] = useState('idle');
  const [preflightError, setPreflightError] = useState('');
  const [micLevel, setMicLevel] = useState(0);
  const [voiceInputConfirmed, setVoiceInputConfirmed] = useState(false);
  const [soundOutputConfirmed, setSoundOutputConfirmed] = useState(false);
  const [speakerTestStatus, setSpeakerTestStatus] = useState('idle');
  const [speakerTestMessage, setSpeakerTestMessage] = useState('');
  const [availableAudioInputs, setAvailableAudioInputs] = useState([]);
  const [availableAudioOutputs, setAvailableAudioOutputs] = useState([]);
  const [selectedAudioInputId, setSelectedAudioInputId] = useState('');
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState('');
  const [joinRequested, setJoinRequested] = useState(false);
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [settingsDrawerFocus, setSettingsDrawerFocus] = useState('devices');
  const [quickstartOpen, setQuickstartOpen] = useState(false);
  const [backgroundBlurLevel, setBackgroundBlurLevel] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!open) {
      try {
        window.sessionStorage.removeItem(ACTIVE_VIDEO_CALL_SESSION_KEY);
      } catch (error) {
        void error;
      }
      return undefined;
    }

    try {
      window.sessionStorage.setItem(ACTIVE_VIDEO_CALL_SESSION_KEY, '1');
    } catch (error) {
      void error;
    }

    return () => {
      try {
        window.sessionStorage.removeItem(ACTIVE_VIDEO_CALL_SESSION_KEY);
      } catch (error) {
        void error;
      }
    };
  }, [open]);
  const [screenShareState, setScreenShareState] = useState('idle');
  const [screenShareMessage, setScreenShareMessage] = useState('');
  const [screenShareHint, setScreenShareHint] = useState('');
  const [workspaceWidthPercent, setWorkspaceWidthPercent] = useState(50);
  const workspaceResizeStateRef = useRef(null);
  const workspaceLayoutRef = useRef(null);
  const supportsAudioOutputSelection = typeof HTMLMediaElement !== 'undefined' && typeof HTMLMediaElement.prototype.setSinkId === 'function';
  const browserName = useMemo(() => getBrowserName(), []);
  const canOpenPatientNavigation = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return workspaceVariant === 'patient'
      || window.location.pathname.startsWith('/admin_dashboard')
      || window.location.pathname.startsWith('/superuser_admin_dashboard');
  }, [workspaceVariant]);
  const isSafariBrowser = browserName === 'Safari';

  const openPatientWorkspaceMenu = useCallback((event) => {
    if (workspaceVariant !== 'patient') {
      setPatientWorkspaceOpen((current) => !current);
      return;
    }

    if (patientWorkspaceMenuAnchorEl) {
      setPatientWorkspaceMenuAnchorEl(null);
      return;
    }

    setPatientWorkspaceOpen(true);
    setPatientWorkspaceMenuAnchorEl(event?.currentTarget || null);
  }, [patientWorkspaceMenuAnchorEl, workspaceVariant]);

  const closePatientWorkspaceMenu = useCallback(() => {
    setPatientWorkspaceMenuAnchorEl(null);
  }, []);

  const selectPatientWorkspaceSection = useCallback((sectionKey) => {
    setPatientWorkspaceSection(sectionKey);
    setPatientWorkspaceOpen(true);
    setPatientWorkspaceMenuAnchorEl(null);
  }, []);

  useEffect(() => {
    if (!open) {
      setPatientWorkspaceOpen(false);
      setPatientWorkspaceMenuAnchorEl(null);
      setPatientWorkspaceSection('feedback');
      setWorkspaceWidthPercent(50);
      return;
    }

    if (workspaceVariant === 'patient') {
      setPatientWorkspaceOpen(true);
    }
  }, [open]);

  useEffect(() => {
    if (!patientWorkspaceOpen) {
      setPatientWorkspaceMenuAnchorEl(null);
    }
  }, [patientWorkspaceOpen]);

  useEffect(() => {
    return () => {
      const resizeState = workspaceResizeStateRef.current;
      if (!resizeState?.moveHandler || !resizeState?.stopHandler) {
        return;
      }

      window.removeEventListener('pointermove', resizeState.moveHandler, true);
      window.removeEventListener('pointerup', resizeState.stopHandler, true);
      window.removeEventListener('pointercancel', resizeState.stopHandler, true);
      workspaceResizeStateRef.current = null;
    };
  }, []);

  const startWorkspaceResize = useCallback((event) => {
    if (!patientWorkspaceOpen || isCompactLayout) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    workspaceResizeStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidthPercent: workspaceWidthPercent,
    };

    const handleWorkspaceResizeMove = (moveEvent) => {
      const resizeState = workspaceResizeStateRef.current;
      const layoutElement = workspaceLayoutRef.current;
      if (!resizeState || !layoutElement || moveEvent.pointerId !== resizeState.pointerId) {
        return;
      }

      const layoutRect = layoutElement.getBoundingClientRect();
      if (!layoutRect.width) {
        return;
      }

      const nextWidth = resizeState.startWidthPercent - ((moveEvent.clientX - resizeState.startX) / layoutRect.width) * 100;
      setWorkspaceWidthPercent(Math.min(70, Math.max(30, nextWidth)));
    };

    const stopWorkspaceResize = () => {
      window.removeEventListener('pointermove', handleWorkspaceResizeMove, true);
      window.removeEventListener('pointerup', stopWorkspaceResize, true);
      window.removeEventListener('pointercancel', stopWorkspaceResize, true);
      workspaceResizeStateRef.current = null;
    };

    workspaceResizeStateRef.current.moveHandler = handleWorkspaceResizeMove;
    workspaceResizeStateRef.current.stopHandler = stopWorkspaceResize;
    window.addEventListener('pointermove', handleWorkspaceResizeMove, true);
    window.addEventListener('pointerup', stopWorkspaceResize, true);
    window.addEventListener('pointercancel', stopWorkspaceResize, true);

    if (event.currentTarget?.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        void error;
      }
    }
  }, [isCompactLayout, patientWorkspaceOpen, workspaceWidthPercent]);

  const stopLocalMedia = useCallback(() => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (speakerTestAudioRef.current) {
      speakerTestAudioRef.current.pause();
      speakerTestAudioRef.current.srcObject = null;
      speakerTestAudioRef.current.src = '';
    }

    if (speakerTestContextRef.current) {
      speakerTestContextRef.current.close().catch(() => {});
      speakerTestContextRef.current = null;
    }

    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = null;
    }

    if (screenShareStreamRef.current) {
      screenShareStreamRef.current.getTracks().forEach((track) => track.stop());
      screenShareStreamRef.current = null;
    }

    screenShareTrackRef.current = null;
  }, []);

  const loadDeviceOptions = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((device) => device.kind === 'audioinput');
    const audioOutputs = supportsAudioOutputSelection ? devices.filter((device) => device.kind === 'audiooutput') : [];

    if (!isMountedRef.current) {
      return;
    }

    const storedAudioInputId = readStoredDeviceId(VIDEO_CALL_MICROPHONE_STORAGE_KEY);
    const storedAudioOutputId = supportsAudioOutputSelection
      ? readStoredDeviceId(VIDEO_CALL_SPEAKER_STORAGE_KEY)
      : '';
    const availableAudioInputIds = new Set(audioInputs.map((device) => device.deviceId));
    const availableAudioOutputIds = new Set(audioOutputs.map((device) => device.deviceId));
    const nextAudioInputId = currentValue => {
      if (currentValue && availableAudioInputIds.has(currentValue)) {
        return currentValue;
      }

      if (storedAudioInputId && availableAudioInputIds.has(storedAudioInputId)) {
        return storedAudioInputId;
      }

      return audioInputs[0]?.deviceId || '';
    };

    const nextAudioOutputId = currentValue => {
      if (!supportsAudioOutputSelection) {
        return '';
      }

      if (currentValue && availableAudioOutputIds.has(currentValue)) {
        return currentValue;
      }

      if (storedAudioOutputId && availableAudioOutputIds.has(storedAudioOutputId)) {
        return storedAudioOutputId;
      }

      return audioOutputs[0]?.deviceId || '';
    };

    setAvailableAudioInputs(audioInputs);
    setAvailableAudioOutputs(audioOutputs);
    setSelectedAudioInputId(nextAudioInputId);
    setSelectedAudioOutputId(nextAudioOutputId);
  }, [supportsAudioOutputSelection]);

  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) {
      return undefined;
    }

    const handleDeviceChange = () => {
      void loadDeviceOptions();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [loadDeviceOptions]);

  const handleTestSoundOutput = useCallback(async () => {
    setSpeakerTestStatus('testing');
    setSpeakerTestMessage('');

    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) {
        throw new Error('This browser cannot run a sound output test.');
      }

      const audioContext = new AudioContextCtor();
      speakerTestContextRef.current = audioContext;

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const destination = audioContext.createMediaStreamDestination();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gainNode.gain.value = 0.05;

      oscillator.connect(gainNode);
      gainNode.connect(destination);

      const speakerAudio = speakerTestAudioRef.current;
      if (!speakerAudio) {
        throw new Error('Audio playback test element is not ready.');
      }

      speakerAudio.srcObject = destination.stream;
      if (selectedAudioOutputId && typeof speakerAudio.setSinkId === 'function') {
        await speakerAudio.setSinkId(selectedAudioOutputId);
      }

      await speakerAudio.play();
      oscillator.start();

      window.setTimeout(() => {
        try {
          oscillator.stop();
        } catch (stopError) {
          void stopError;
        }

        speakerAudio.pause();
        speakerAudio.srcObject = null;
        audioContext.close().catch(() => {});
        speakerTestContextRef.current = null;
      }, 700);

      setSpeakerTestStatus('ready');
      setSoundOutputConfirmed(true);
      setSpeakerTestMessage(
        typeof speakerAudio.setSinkId === 'function' && selectedAudioOutputId
          ? 'Sound output tested on the selected hardware.'
          : 'Sound output tested on the default speaker.'
      );
    } catch (soundError) {
      setSpeakerTestStatus('error');
      setSpeakerTestMessage(soundError?.message || 'Sound output test failed.');
    }
  }, [selectedAudioOutputId]);

  const startDeviceCheck = useCallback(async () => {
    setPreflightStatus('checking');
    setPreflightError('');
    setMicLevel(0);
    setVoiceInputConfirmed(false);
    setSpeakerTestStatus('idle');
    setSpeakerTestMessage('');

    stopLocalMedia();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedAudioInputId ? { deviceId: { exact: selectedAudioInputId } } : true,
        video: true,
      });

      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      mediaStreamRef.current = stream;

      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        previewVideoRef.current.muted = true;
        previewVideoRef.current.playsInline = true;
        previewVideoRef.current.autoplay = true;
        await previewVideoRef.current.play().catch(() => {});
      }

      await loadDeviceOptions();

      if (isSafariBrowser) {
        window.setTimeout(() => {
          if (isMountedRef.current) {
            void loadDeviceOptions();
          }
        }, 350);
      }

      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (AudioContextCtor) {
        const audioContext = new AudioContextCtor();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateMicLevel = () => {
          if (!analyserRef.current || !isMountedRef.current) {
            return;
          }

          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
          const normalizedLevel = Math.min(Math.max((average / 255) * 100, 5), 100);
          setMicLevel(normalizedLevel);
          animationFrameRef.current = window.requestAnimationFrame(updateMicLevel);
        };

        updateMicLevel();
      }

      setPreflightStatus('ready');
      setVoiceInputConfirmed(true);
    } catch (deviceError) {
      setPreflightStatus('error');
      setPreflightError(deviceError?.message || 'Camera or microphone could not be accessed.');
    }
  }, [isSafariBrowser, loadDeviceOptions, selectedAudioInputId, stopLocalMedia]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopLocalMedia();
    };
  }, [stopLocalMedia]);

  const isDemoMode = !appointment?.id;
  const demoViewerRoleLabel = workspaceVariant === 'patient' ? 'Patient' : 'Clinician';
  const demoCounterpartRoleLabel = workspaceVariant === 'patient' ? 'Test clinician' : 'Test patient';

  const appointmentLabel = useMemo(() => {
    if (isDemoMode) return `${demoViewerRoleLabel} test call`;
    if (!appointment) return 'Video Call';
    return appointment.title || appointment.service_name || appointment.appointment_type || 'Video Call';
  }, [appointment, demoViewerRoleLabel, isDemoMode]);

  const appointmentSubtitle = useMemo(() => {
    if (isDemoMode) {
      return workspaceVariant === 'patient'
        ? 'No booking is attached. Use this space to test your setup as a patient with a test clinician on the other side.'
        : 'No booking is attached. Use this space to test your setup as a clinician with a test patient on the other side.';
    }

    return getAppointmentSubtitle(appointment, appointmentLabel);
  }, [appointment, appointmentLabel, isDemoMode, workspaceVariant]);

  const consultationSummary = useMemo(() => (
    normalizeVideoconsultationSummary(
      appointment?.videoconsultation_summary
      || appointment?.video_session?.videoconsultation_summary
      || null,
    )
  ), [appointment]);

  const isWaitingRoom = Boolean(videoSession?.waiting_room);
  const liveStatusLabel = isWaitingRoom
    ? 'Virtual waiting room'
    : sessionInfo
      ? 'Live consultation'
      : 'Preparing room';
  const liveStatusDetail = isWaitingRoom
    ? `${videoSession?.participant_count || 1} patient${(videoSession?.participant_count || 1) === 1 ? '' : 's'} waiting`
    : isDemoMode
      ? `${demoViewerRoleLabel} demo - ${demoCounterpartRoleLabel} layout`
      : appointment?.clinic_location_name || appointment?.room_name || 'Secure LiveKit room';

  const summaryChips = useMemo(() => {
    if (!consultationSummary) {
      return [];
    }

    return [
      ...(consultationSummary.reportedSpecialities || []).map((item) => ({ key: `speciality-${item}`, label: formatVideoconsultationSummaryLabel(item) })),
      ...(consultationSummary.reportedSymptomTags || []).map((item) => ({ key: `symptom-${item}`, label: formatVideoconsultationSummaryLabel(item) })),
      ...(consultationSummary.redFlagTags || []).map((item) => ({ key: `red-flag-${item}`, label: formatVideoconsultationSummaryLabel(item) })),
    ];
  }, [consultationSummary]);

  useEffect(() => {
    setVideoSession(appointment?.video_session || null);
  }, [appointment?.video_session]);

  useEffect(() => {
    if (!open) {
      stopLocalMedia();
      setSessionInfo(null);
      setError('');
      setLoading(false);
      setElapsedLabel('00:00');
      setPreflightStatus('idle');
      setPreflightError('');
      setMicLevel(0);
      setVoiceInputConfirmed(false);
      setSoundOutputConfirmed(false);
      setSpeakerTestStatus('idle');
      setSpeakerTestMessage('');
      setAvailableAudioInputs([]);
      setAvailableAudioOutputs([]);
      setSelectedAudioInputId('');
      setSelectedAudioOutputId('');
      setJoinRequested(false);
      setSettingsDrawerOpen(false);
      setSettingsDrawerFocus('devices');
      setQuickstartOpen(false);
      setBackgroundBlurLevel(0);
      setScreenShareState('idle');
      setScreenShareMessage('');
      setScreenShareHint('');
      return;
    }

    setPreflightStatus('idle');
    setPreflightError('');
    setMicLevel(0);
    setVoiceInputConfirmed(false);
    setSoundOutputConfirmed(false);
    setSpeakerTestStatus('idle');
    setSpeakerTestMessage('');
    setJoinRequested(false);
    setSettingsDrawerOpen(false);
    setSettingsDrawerFocus('devices');
    setQuickstartOpen(false);
    setScreenShareMessage('');
    setScreenShareHint('');

    void loadDeviceOptions();
  }, [open, loadDeviceOptions, stopLocalMedia]);

  useEffect(() => {
    if (!open || !joinRequested || !appointment?.id || !authToken) {
      setSessionInfo(null);
      setLoading(false);
      setElapsedLabel('00:00');
      return;
    }

    let cancelled = false;

    const loadVideoSession = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${BACKEND_URL}/api/calendar/appointments/${appointment.id}/video-session/`, {
          headers: {
            Authorization: `Token ${authToken}`,
            Accept: 'application/json',
          },
        });

        const payload = await response.json().catch(() => ({}));
        if (!cancelled && response.ok) {
          setVideoSession(payload);
        }

        const tokenResponse = await fetch(`${BACKEND_URL}/api/calendar/appointments/${appointment.id}/video-token/`, {
          method: 'POST',
          headers: {
            Authorization: `Token ${authToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ consent_accepted: true }),
        });

        const tokenPayload = await tokenResponse.json().catch(() => ({}));
        if (!tokenResponse.ok) {
          throw new Error(tokenPayload?.error || 'Failed to join the videocall');
        }

        if (!cancelled) {
          setSessionInfo(tokenPayload);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setSessionInfo(null);
          setError(fetchError.message || 'Failed to join the videocall');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadVideoSession();

    return () => {
      cancelled = true;
    };
  }, [open, joinRequested, appointment?.id, appointment?.video_session, authToken]);

  const handleDisconnect = () => {
    stopLocalMedia();
    setSessionInfo(null);
    setJoinRequested(false);
    setError('The videocall connection ended. You can close this window or rejoin the call.');
    setSettingsDrawerOpen(false);
    setScreenShareState('idle');
    setScreenShareMessage('');
    setScreenShareHint('');
  };

  const handleCloseCall = () => {
    stopLocalMedia();
    setSessionInfo(null);
    setError('');
    setJoinRequested(false);
    setLoading(false);
    setSettingsDrawerOpen(false);
    setScreenShareState('idle');
    setScreenShareMessage('');
    setScreenShareHint('');
    onClose();
  };

  const handleContinueToWaitingRoom = () => {
    setError('');
    setJoinRequested(true);
  };

  const openSettingsDrawer = useCallback((section = 'devices') => {
    setSettingsDrawerFocus(section);
    setSettingsDrawerOpen(true);
  }, []);

  const stopScreenShareTracks = useCallback((message = 'Screen share stopped.') => {
    if (screenShareTrackRef.current) {
      try {
        screenShareTrackRef.current.stop();
      } catch (trackError) {
        void trackError;
      }
      screenShareTrackRef.current = null;
    }

    screenSharePublishedTrackRef.current = null;

    if (screenShareStreamRef.current) {
      screenShareStreamRef.current.getTracks().forEach((track) => track.stop());
      screenShareStreamRef.current = null;
    }

    setScreenShareState('idle');
    setScreenShareMessage(message);
    setScreenShareHint('');
  }, []);

  const handleStartScreenShare = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('This browser does not support screen sharing.');
      }

      setScreenShareMessage('Choose a screen, window, or browser tab to share.');
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'monitor',
        },
        audio: false,
      });

      const [displayTrack] = displayStream.getVideoTracks();
      if (!displayTrack) {
        throw new Error('No screen capture track was returned.');
      }

      if (screenShareStreamRef.current) {
        stopScreenShareTracks('Screen share restarted.');
      }

      screenShareStreamRef.current = displayStream;
      screenShareTrackRef.current = displayTrack;
      setScreenShareState('pending');
      setScreenShareHint('Screen share is ready and will publish when the room connects.');
      setScreenShareMessage('Screen selected.');

      displayTrack.addEventListener('ended', () => {
        stopScreenShareTracks();
      }, { once: true });
    } catch (shareError) {
      setScreenShareState('idle');
      setScreenShareMessage(shareError?.message || 'Unable to start screen sharing.');
    }
  }, [stopScreenShareTracks]);

  const handleToggleScreenShare = useCallback(() => {
    if (screenShareState === 'idle') {
      void handleStartScreenShare();
      return;
    }

    stopScreenShareTracks();
  }, [handleStartScreenShare, screenShareState, stopScreenShareTracks]);

  const handleStartSafariSetup = useCallback(async () => {
    setQuickstartOpen(false);
    await startDeviceCheck();
    if (browserName === 'Safari') {
      await handleTestSoundOutput();
    }
  }, [browserName, handleTestSoundOutput, startDeviceCheck]);

  const handleScreenSharePublished = useCallback(() => {
    setScreenShareState('live');
    setScreenShareMessage('Screen sharing is live.');
    setScreenShareHint('');
  }, []);

  const handleScreenShareStopped = useCallback((message = 'Screen share stopped.') => {
    setScreenShareState('idle');
    setScreenShareMessage(message);
    setScreenShareHint('');
  }, []);

  const renderVolumeLevelIcon = (active, testing) => (
    <Box className={classes.volumeIcon} aria-hidden="true">
      <Box className={`${classes.volumeColumn} ${classes.volumeColumnLow} ${active ? (testing ? classes.volumeColumnTesting : classes.volumeColumnActive) : ''}`} />
      <Box className={`${classes.volumeColumn} ${classes.volumeColumnMid} ${active ? (testing ? classes.volumeColumnTesting : classes.volumeColumnActive) : ''}`} />
      <Box className={`${classes.volumeColumn} ${classes.volumeColumnHigh} ${active ? (testing ? classes.volumeColumnTesting : classes.volumeColumnActive) : ''}`} />
    </Box>
  );

  const renderDemoRoom = () => (
    <Box className={classes.callStage}>
      <Box className={classes.tilePanel} style={{ minHeight: 'clamp(420px, 56vh, 720px)' }}>
        <Typography className={classes.tileHeader}>
          {workspaceVariant === 'patient' ? 'Test clinician' : 'Test patient'}
        </Typography>
        <Box className={classes.tileSurface}>
          <Box className={classes.panelPlaceholder}>
            <Box>
              <Typography className={classes.panelPlaceholderTitle}>
                {workspaceVariant === 'patient' ? 'Test clinician' : 'Test patient'}
              </Typography>
              <Typography className={classes.panelPlaceholderBody}>
                {workspaceVariant === 'patient'
                  ? 'This frame mirrors the clinician camera area used in a real videocall.'
                  : 'This frame mirrors the patient camera area used in a real videocall.'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <WorkspacePiP
        classes={classes}
        blurLevel={backgroundBlurLevel}
        mediaStream={mediaStreamRef.current}
        participant={null}
      />

    </Box>
  );

  const renderLiveRoom = () => {
    if (!joinRequested) {
      return (
        <Box className={classes.preflightShell} style={{ minWidth: 0 }}>
            <Box className={classes.preflightGrid}>
            <Box className={classes.videoPreviewPanel}>
              {mediaStreamRef.current ? (
                <BackgroundAwareSelfView classes={classes} mediaStream={mediaStreamRef.current} blurLevel={backgroundBlurLevel} />
              ) : (
                <video ref={previewVideoRef} className={classes.previewVideo} playsInline muted autoPlay />
              )}
              {!mediaStreamRef.current && (
                <Box className={classes.previewPlaceholder}>
                  <Box>
                    <Typography style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>
                      Camera preview will appear here
                    </Typography>
                    <Typography style={{ marginTop: 8, lineHeight: 1.5 }}>
                      Allow access if you want to confirm the camera before joining.
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box className={classes.videoStatusBadge}>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon style={{ color: '#fff', fontSize: 16 }} />}
                  label={mediaStreamRef.current ? 'Ready' : 'Video pending'}
                  className={classes.videoStatusChip}
                />
              </Box>
            </Box>

                <Box className={classes.controlStack}>
                <Box className={classes.devicePair} style={{ marginTop: 0 }}>
                  <Box className={classes.deviceCheckCard}>
                    <Box className={classes.deviceRow}>
                      <Box className={classes.audioIconCell}>
                        {renderVolumeLevelIcon(preflightStatus === 'checking' || voiceInputConfirmed, preflightStatus === 'checking')}
                      </Box>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        select
                        SelectProps={{
                          MenuProps: AUDIO_SELECT_MENU_PROPS,
                          displayEmpty: true,
                        }}
                        className={`${classes.orangeFocused} ${classes.deviceSelectField}`}
                        value={selectedAudioInputId}
                        onChange={(event) => {
                          const nextAudioInputId = event.target.value;
                          setSelectedAudioInputId(nextAudioInputId);
                          writeStoredDeviceId(VIDEO_CALL_MICROPHONE_STORAGE_KEY, nextAudioInputId);
                          setVoiceInputConfirmed(false);
                        }}
                      >
                        <MenuItem value="">Default microphone</MenuItem>
                        {availableAudioInputs.map((device, index) => (
                          <MenuItem key={device.deviceId || `mic-${index}`} value={device.deviceId}>
                            {getDeviceOptionLabel(device, index, 'Microphone')}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box className={classes.deviceActionCell}>
                        <Button
                          variant="outlined"
                          className={`${classes.deviceActionButton} ${classes.cancelActionButton}`}
                          onClick={startDeviceCheck}
                          disabled={preflightStatus === 'checking'}
                          aria-label={voiceInputConfirmed ? 'Microphone confirmed' : 'Test'}
                          title={voiceInputConfirmed ? 'Microphone confirmed' : 'Test'}
                        >
                          {preflightStatus === 'checking'
                            ? 'Testing...'
                            : voiceInputConfirmed
                              ? <CheckCircleIcon style={{ fontSize: 18 }} />
                              : 'Test'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>

                  <Box className={classes.deviceCheckCard}>
                    {!supportsAudioOutputSelection ? (
                      <Typography className={classes.deviceCheckValue} style={{ marginBottom: 8 }}>
                        Safari can usually test sound on the default output, but it may not expose every speaker or headphone device.
                      </Typography>
                    ) : null}
                    <Box className={classes.deviceRow}>
                      <Box className={classes.audioIconCell}>
                        {renderVolumeLevelIcon(speakerTestStatus === 'testing' || soundOutputConfirmed, speakerTestStatus === 'testing')}
                      </Box>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        select
                        SelectProps={{
                          MenuProps: AUDIO_SELECT_MENU_PROPS,
                          displayEmpty: true,
                        }}
                        className={`${classes.orangeFocused} ${classes.deviceSelectField}`}
                        value={selectedAudioOutputId}
                        disabled={!supportsAudioOutputSelection}
                        onChange={(event) => {
                          const nextAudioOutputId = event.target.value;
                          setSelectedAudioOutputId(nextAudioOutputId);
                          writeStoredDeviceId(VIDEO_CALL_SPEAKER_STORAGE_KEY, nextAudioOutputId);
                          setSoundOutputConfirmed(false);
                        }}
                      >
                        <MenuItem value="">Default speakers</MenuItem>
                        {availableAudioOutputs.map((device, index) => (
                          <MenuItem key={device.deviceId || `speaker-${index}`} value={device.deviceId}>
                            {getDeviceOptionLabel(device, index, 'Speaker')}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box className={classes.deviceActionCell}>
                        <Button
                          variant="outlined"
                          className={`${classes.deviceActionButton} ${classes.cancelActionButton}`}
                          onClick={() => void handleTestSoundOutput()}
                          disabled={speakerTestStatus === 'testing' || preflightStatus === 'checking'}
                          aria-label={soundOutputConfirmed ? 'Speakers confirmed' : 'Test'}
                          title={soundOutputConfirmed ? 'Speakers confirmed' : 'Test'}
                        >
                          {speakerTestStatus === 'testing'
                            ? 'Testing...'
                            : soundOutputConfirmed
                              ? <CheckCircleIcon style={{ fontSize: 18 }} />
                              : 'Test'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Tooltip title={(!voiceInputConfirmed || !soundOutputConfirmed)
                  ? 'Test microphone and speakers first'
                  : (isDemoMode ? 'Start the test call' : 'Enter the videocall')}
                >
                  <span>
                    <Button
                      variant="contained"
                      className={`${classes.confirmActionButton} ${classes.joinCallButton}`}
                      onClick={handleContinueToWaitingRoom}
                      disabled={preflightStatus === 'checking' || loading || !voiceInputConfirmed || !soundOutputConfirmed}
                    >
                      {isDemoMode ? 'Start test call' : 'Enter videocall'}
                    </Button>
                  </span>
                </Tooltip>
                </Box>
            </Box>
        </Box>
      );
    }

    if (isDemoMode) {
      return renderDemoRoom();
    }

    if (loading) {
      return (
        <Box className={classes.loadingShell}>
          <CircularProgress style={{ color: '#fff' }} />
          <Typography style={{ color: 'rgba(255, 255, 255, 0.84)' }}>
            Preparing the call room...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box className={classes.errorShell}>
          <Box>
            <Typography className={classes.errorTitle}>
              {sessionInfo ? 'Call disconnected' : 'Unable to join the call'}
            </Typography>
            <Typography className={classes.errorText} style={{ marginTop: 10 }}>
              {error}
            </Typography>
          </Box>
          <Box style={{ display: 'flex', gap: 12 }}>
            <Button
              variant="outlined"
              onClick={handleCloseCall}
              style={{ borderColor: '#fff', color: '#fff', fontWeight: 700, borderRadius: 18 }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleContinueToWaitingRoom}
              style={{ backgroundColor: '#fff', color: '#08111f', fontWeight: 700, borderRadius: 18 }}
            >
              Rejoin call
            </Button>
          </Box>
        </Box>
      );
    }

    if (!sessionInfo) {
      return (
        <Box className={classes.loadingShell}>
          <CircularProgress style={{ color: '#fff' }} />
          <Typography style={{ color: 'rgba(255, 255, 255, 0.84)' }}>
            Preparing the call room...
          </Typography>
        </Box>
      );
    }

    return (
      <LiveKitRoom
        token={sessionInfo.token}
        serverUrl={sessionInfo.livekit_url}
        connect={open}
        audio
        video
        onDisconnected={handleDisconnect}
        style={{ height: '100%', minHeight: 0 }}
      >
        <ScreenSharePublisherBridge
          screenShareState={screenShareState}
          screenShareStreamRef={screenShareStreamRef}
          screenShareTrackRef={screenShareTrackRef}
          screenSharePublishedTrackRef={screenSharePublishedTrackRef}
          onScreenShareLive={handleScreenSharePublished}
          onScreenShareStopped={handleScreenShareStopped}
        />
        <LiveCallStage
          classes={classes}
          mediaStreamRef={mediaStreamRef}
          blurLevel={backgroundBlurLevel}
        />
      </LiveKitRoom>
    );
  };

  useEffect(() => {
    const startedAt = videoSession?.started_at;
    if (!startedAt) {
      setElapsedLabel('00:00');
      return undefined;
    }

    const formatElapsed = () => {
      const startedTime = new Date(startedAt).getTime();
      const now = Date.now();
      if (Number.isNaN(startedTime) || now <= startedTime) {
        return '00:00';
      }

      const totalSeconds = Math.floor((now - startedTime) / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      return `${minutes}:${seconds}`;
    };

    setElapsedLabel(formatElapsed());
    const timerId = window.setInterval(() => {
      setElapsedLabel(formatElapsed());
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [videoSession?.started_at]);

  useEffect(() => {
    if (!open || !joinRequested || !appointment?.id || !authToken) {
      return undefined;
    }

    let cancelled = false;

    const pollSessionState = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/calendar/appointments/${appointment.id}/video-session/`, {
          headers: {
            Authorization: `Token ${authToken}`,
            Accept: 'application/json',
          },
        });

        const payload = await response.json().catch(() => ({}));
        if (!cancelled && response.ok) {
          setVideoSession(payload);
        }
      } catch (pollError) {
        void pollError;
      }
    };

    void pollSessionState();
    const pollIntervalId = window.setInterval(() => {
      void pollSessionState();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(pollIntervalId);
    };
  }, [open, joinRequested, appointment?.id, authToken]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseCall}
      fullScreen
      PaperProps={{
        className: classes.paper,
      }}
    >
      <Box className={classes.shell}>
        <Box className={classes.glow} />
        <audio ref={speakerTestAudioRef} hidden />

        <DialogTitle className={classes.header} disableTypography>
          <Box className={classes.headerRow}>
            <Box className={classes.headerCopy}>
              <Typography className={classes.title}>
                {appointmentLabel}
              </Typography>
              <Typography className={classes.subtitle}>
                {isDemoMode
                  ? 'Videoconsultation test call'
                  : `Videoconsultation${appointment?.start_datetime ? ` · ${formatAppointmentRange(appointment)}` : ''}`}
              </Typography>
            </Box>

            <Box className={classes.headerStatus}>
              <Chip
                size="small"
                label={videoSession?.started_at ? `Live ${elapsedLabel}` : liveStatusLabel}
                className={classes.statusChip}
              />
            </Box>

            <Box className={classes.headerMeta}>
              <Tooltip title="Call controls">
                <IconButton
                  onClick={() => openSettingsDrawer('devices')}
                  className={classes.headerActionButton}
                  aria-label="Open call controls"
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              {workspaceVariant === 'patient' && patientWorkspaceOpen ? (
                <Tooltip title="Close workspace">
                  <IconButton
                    onClick={() => setPatientWorkspaceOpen(false)}
                    className={`${classes.headerActionButton} ${classes.workspaceHeaderActionButton}`}
                    aria-label="Close workspace"
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              {canOpenPatientNavigation ? (
                <Tooltip title={workspaceVariant === 'patient' ? 'Choose patient workspace section' : (patientWorkspaceOpen ? 'Hide patient workspace' : 'Open patient workspace')}>
                  <IconButton
                    onClick={openPatientWorkspaceMenu}
                    className={`${classes.headerActionButton} ${patientWorkspaceOpen ? classes.patientNavigationIconButtonActive : ''}`}
                    aria-label={workspaceVariant === 'patient' ? 'Open patient workspace menu' : 'Toggle patient workspace'}
                    aria-haspopup={workspaceVariant === 'patient' ? 'menu' : undefined}
                    aria-expanded={workspaceVariant === 'patient' ? Boolean(patientWorkspaceMenuAnchorEl) : undefined}
                  >
                    <AppsIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              {joinRequested ? (
                <Tooltip title="Leave videocall">
                  <IconButton
                    className={classes.endButton}
                    onClick={handleDisconnect}
                    aria-label="Leave videocall"
                  >
                    <CallEndIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Close videocall interface">
                <IconButton
                  onClick={handleCloseCall}
                  className={classes.headerActionButton}
                  aria-label="Close videocall interface"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>

        {workspaceVariant === 'patient' ? (
          <Menu
            anchorEl={patientWorkspaceMenuAnchorEl}
            open={Boolean(patientWorkspaceMenuAnchorEl)}
            onClose={closePatientWorkspaceMenu}
            keepMounted
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ style: { minWidth: 280, borderRadius: 16 } }}
          >
            {PATIENT_WORKSPACE_SECTIONS.map((section) => (
              <MenuItem
                key={section.key}
                selected={patientWorkspaceSection === section.key}
                onClick={() => selectPatientWorkspaceSection(section.key)}
              >
                <Box style={{ display: 'grid', gap: 2 }}>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 14 }}>
                    {section.label}
                  </Typography>
                  <Typography style={{ color: '#64748B', fontSize: 12, lineHeight: 1.35 }}>
                    {section.hint}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        ) : null}

        <DialogContent className={classes.content}>
          <Box
            ref={workspaceLayoutRef}
            className={`${classes.layout} ${patientWorkspaceOpen ? classes.layoutWithWorkspace : ''}`}
            style={patientWorkspaceOpen && !isCompactLayout ? {
              gridTemplateColumns: `minmax(0, ${100 - workspaceWidthPercent}%) 14px minmax(0, ${workspaceWidthPercent}%)`,
            } : undefined}
          >
            <Box className={classes.sessionPanel}>
              <Box className={classes.roomCard}>
                <Box className={classes.roomInner}>
                  {renderLiveRoom()}
                </Box>
              </Box>

            </Box>

            {patientWorkspaceOpen ? (
              <Box className={classes.workspaceSplitter}>
                <Box
                  className={classes.workspaceSplitterHandle}
                  onPointerDown={startWorkspaceResize}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize workspace width"
                  tabIndex={0}
                >
                  <SwapHorizIcon fontSize="small" />
                </Box>
              </Box>
            ) : null}

            <VideoCallPatientWorkspacePanel
              open={patientWorkspaceOpen}
              onClose={() => setPatientWorkspaceOpen(false)}
              classes={classes}
              workspaceVariant={workspaceVariant}
              authToken={authToken}
              treatmentId={patientTreatmentId}
              activeSectionKey={patientWorkspaceSection}
            />
          </Box>
        </DialogContent>
        <VideoCallSettingsDrawer
          open={settingsDrawerOpen}
          onClose={() => setSettingsDrawerOpen(false)}
          classes={classes}
          isMobile={isMobile}
          supportsAudioOutputSelection={supportsAudioOutputSelection}
          selectedAudioInputId={selectedAudioInputId}
          setSelectedAudioInputId={setSelectedAudioInputId}
          selectedAudioOutputId={selectedAudioOutputId}
          setSelectedAudioOutputId={setSelectedAudioOutputId}
          availableAudioInputs={availableAudioInputs}
          availableAudioOutputs={availableAudioOutputs}
          onTestMicrophone={startDeviceCheck}
          onTestSpeakers={() => void handleTestSoundOutput()}
          voiceInputConfirmed={voiceInputConfirmed}
          soundOutputConfirmed={soundOutputConfirmed}
          preflightStatus={preflightStatus}
          speakerTestStatus={speakerTestStatus}
          blurLevel={backgroundBlurLevel}
          setBlurLevel={setBackgroundBlurLevel}
          screenShareState={screenShareState}
          screenShareMessage={screenShareMessage}
          shareScreenHint={screenShareHint}
          onToggleScreenShare={handleToggleScreenShare}
          onOpenQuickstart={() => setQuickstartOpen(true)}
        />
        <VideoCallQuickstartDialog
          open={quickstartOpen}
          onClose={() => setQuickstartOpen(false)}
          classes={classes}
          browserName={browserName}
          onStartSafariSetup={handleStartSafariSetup}
        />
      </Box>
    </Dialog>
  );
};

export default VideoCallModal;